/**
 * Every UI string of the web panel. Components never hard-code copy. Tone: short, lead with the
 * result, no blame, no emoji.
 *
 * D9: this file is the panel's English and the i18next `panel` EN resource (`src/i18n`). Its Turkish
 * lives in `@sitelog/shared` (`UI_RESOURCES.tr.panel`), keyed by the paths here. Converted
 * components read it through `useCopy().t('path')`, so the panel's language applies:
 * - only plain strings are i18next resources: interpolations there use `{{name}}`, and counted
 *   phrases are `key_one` / `key_other` pairs read as `t('key', { count })`;
 * - pages not converted yet still import `copy` and call its functions (they render in English
 *   under `<EnglishOnly>`, see `src/app/routes.tsx`).
 */

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export const copy = {
  app: {
    name: 'TalkOlog',
    tagline: 'Talk. It’s logged.',
    skipToContent: 'Skip to content',
  },

  nav: {
    overview: 'Overview',
    sites: 'Sites',
    reports: 'Reports',
    hours: 'Crew hours',
    weather: 'Weather & delays',
    setupLabel: 'Setup',
    team: 'Team & numbers',
    glossary: 'Site glossary',
    settings: 'Settings',
    billing: 'Billing',
    try: 'Try it on WhatsApp',
    /** v1.2 `/dev/phone` (§11.1): texts from the `console` SMS driver (§5.2). */
    devPhone: 'Demo phone',
    badges: {
      reportsToReview_one: '{{count}} report to review',
      reportsToReview_other: '{{count}} reports to review',
      unknownSenders_one: '{{count}} unknown number',
      unknownSenders_other: '{{count}} unknown numbers',
      termsToReview_one: '{{count}} term to review',
      termsToReview_other: '{{count}} terms to review',
    },
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    mainNavigation: 'Main navigation',
  },

  shell: {
    orgSwitcher: {
      label: 'Switch company',
      activeSites_one: '{{count}} active site',
      activeSites_other: '{{count}} active sites',
      currentOrg: 'Current company',
      otherOrgs: 'Your companies',
    },
    search: {
      /** Web-Overview artboard, verbatim. */
      placeholder: 'Search reports, sites, subcontractors',
      label: 'Search',
      clear: 'Clear search',
      noResults: 'Nothing found for “{{query}}”.',
      hint: 'Type at least 2 characters',
      groups: {
        report: 'Reports',
        site: 'Sites',
        company: 'Companies',
        transcript: 'Transcripts',
        reporter: 'Reporters',
        term: 'Glossary',
      },
    },
    notifications: {
      label: 'Notifications',
      /** Joined to `label` as "Notifications, 3 new" (Web-Overview artboard). */
      unread_one: '{{count}} new',
      unread_other: '{{count}} new',
      title: 'Notifications',
      empty: 'Nothing new. Reports and alerts will show up here.',
      markAllRead: 'Mark all read',
      /**
       * A notice's wording by its data key (`notifications.data`, packages/shared notifications.ts
       * `noticeDataSchema`), in the reader's language. A key without `body` shows the stored body
       * (a change note, a heard snippet, terms, a provider's error). Rows without data show their
       * stored English title and body.
       */
      notices: {
        late_upload: {
          titleAudio: '{{site}} — late voice note for {{date}}',
          titlePhoto: '{{site}} — late photo for {{date}}',
          body: '{{reporter}} recorded it on that day, and it arrived after the day’s reporting window had closed. It is on report {{reportNo}}, marked late.',
          bodyUnnamed:
            'The site manager recorded it on that day, and it arrived after the day’s reporting window had closed. It is on report {{reportNo}}, marked late.',
        },
        missing_report: {
          title: '{{site}} — no report',
          body: '{{first}} hasn’t sent today’s report, and the deadline has passed.',
          bodyUnnamed: 'The site manager hasn’t sent today’s report, and the deadline has passed.',
          bodyReminders_one: '{{first}} didn’t answer {{count}} reminder.',
          bodyReminders_other: '{{first}} didn’t answer {{count}} reminders.',
        },
        report_edited: { title: '{{reportNo}} was edited after sign-off' },
        needs_review: { title: '{{reportNo}} needs review' },
        glossary_review: {
          title_one: '{{count}} new term to review',
          title_other: '{{count}} new terms to review',
        },
        pack_ready: {
          title: 'EOT evidence pack ready: {{site}}',
          titleAllSites: 'EOT evidence pack ready: all sites',
          body: '{{from}} – {{to}}',
        },
        processing_delayed: { title: '{{reportNo}} is taking longer than usual' },
        processing_failed: {
          title: '{{reportNo}}: processing stopped',
          body: 'TalkOlog tried {{tries}} times over about three hours and has stopped ({{job}}). The reporter may be waiting for their draft. TalkOlog has been alerted; open the report to check its sources.',
        },
        message_not_sent: {
          title: '{{name}}: a message to them was not sent',
          body: 'TalkOlog tried {{tries}} times over about three hours and has stopped ({{job}}). They may be waiting for an answer; contact them directly if it matters today. TalkOlog has been alerted.',
        },
        message_not_handled: {
          title: '{{name}}: a message from them was not handled',
          body: 'TalkOlog tried {{tries}} times over about three hours and has stopped ({{job}}). They may be waiting for an answer; contact them directly if it matters today. TalkOlog has been alerted.',
        },
        message_opted_out: {
          title: '{{name}}: a message to them was not sent',
          body: 'Their number is on the SMS provider’s opt-out list, so TalkOlog texts them nothing until they reply START.',
        },
        message_refused: {
          title: '{{name}}: a message to them was not sent',
          body: 'The SMS provider refused their number ({{error}}). Check the number in Team & numbers.',
        },
        message_channels_failed: {
          title: '{{name}}: a message to them was not sent',
          body: 'TalkOlog tried every message channel it has for them ({{channels}}) and none could deliver the message ({{error}}). Check their number and channels in Team & numbers.',
        },
        message_account_fault: {
          title: '{{name}}: a text to them failed at the SMS provider',
          titleUnnamed: 'A text failed at the SMS provider',
          body: 'Texts are failing for this account ({{error}}). The SMS provider accepted the text and then refused it for the account or the sender, not for the reporter’s number. TalkOlog tries their other message channels, if they have any. Other texts that fail this way in the next hour are not notified again.',
        },
        message_no_sender: {
          title: '{{name}}: a message to them was not sent',
          body: 'No SMS or WhatsApp sender is set for this organisation’s country in this environment, so the message reached no phone.',
        },
        welcome_undeliverable: { title: '{{name}}: the welcome text could not be delivered' },
        welcome_provider_fault: {
          title: '{{name}}: the welcome text failed at the SMS provider',
          body: 'Texts are failing for this account ({{error}}). TalkOlog has been alerted; the reporter was not marked as invited. Re-send the welcome from Team & numbers once the provider is back.',
        },
        welcome_not_sent: {
          title: '{{name}}: the welcome text was not sent',
          bodies: {
            opted_out:
              'They opted out of texts, so TalkOlog texts them nothing until they opt in again (START, on packs that take replies).',
            no_consent:
              'They declined messages when asked for consent, so TalkOlog sends them nothing by text or WhatsApp.',
            no_channel:
              'No message channel of their country can carry the welcome and its link. Contact them another way.',
            console_real_org:
              'Messages to this country are not live in this environment yet: no SMS or WhatsApp sender is set for its country pack, so the reporter got nothing. Once a sender is set, re-send the welcome from Team & numbers.',
          },
        },
        welcome_carrier_undelivered: {
          title: '{{name}}: the welcome text could not be delivered',
          body: 'The carrier could not deliver the welcome text ({{error}}): the number may be a landline, blocked or out of service. Check it on Team & numbers, or contact them another way.',
        },
        welcome_account_fault: {
          title: '{{name}}: the welcome text failed at the SMS provider',
          titleUnnamed: 'A welcome text failed at the SMS provider',
          body: 'Texts are failing for this account ({{error}}). The SMS provider accepted the welcome text and then refused it for the account or the sender, not for the reporter’s number. Re-send the welcome from Team & numbers once the provider account is fixed. Other texts that fail this way in the next hour are not notified again.',
        },
        reporter_opted_out: {
          title: '{{name}} opted out of texts',
          body: '{{phone}} is on the SMS provider’s opt-out list (they replied STOP to this sender before), so TalkOlog texts them nothing until they reply START. Call them if you need their reports.',
        },
        reporter_stop: {
          title: '{{name}} opted out of texts (STOP)',
          body: '{{phone}} replied STOP, so TalkOlog texts them nothing until they reply START. Call them if you need their reports.',
        },
        unknown_contact: { title: 'Unknown number tried to report' },
        reporter_consent_revoked: {
          titles: {
            recording: '{{name}} turned off consent to the recording of their reports',
            data_processing: '{{name}} turned off consent to the processing of their reports',
          },
          body: 'They turned it off in Settings on their phone, so TalkOlog takes no reports from them until they consent again on their phone. Contact them if that is unexpected.',
        },
        device_verify_locked: {
          title: '{{name}}’s phone check is locked for an hour',
          body: 'Someone entered 5 wrong codes on their link within an hour, so TalkOlog refuses new codes for an hour. If it was not them, their link may be in the wrong hands: contact them.',
        },
        reporter_wrong_site: {
          title: '{{name}} says the site is wrong',
          body: 'Check the site assignment on the Team page.',
        },
        payment_failed: {
          title: 'Payment failed',
          body: 'Stripe could not charge your card. Update the payment method in Billing to keep reports and reminders flowing.',
        },
        subscription_canceled: {
          title: 'Subscription canceled',
          body: 'Reminders and new site-days stop until the subscription is restarted from Billing.',
        },
        late_report: {
          title: '{{site}} — late report in',
          body: '{{reportNo}} · {{crew}} on site · {{hours}}',
        },
        invoice_gap: {
          title: 'Invoice gap: {{company}} {{sign}}{{gap}}',
          body: '{{invoiceNo}} invoices {{invoiced}} · reports show {{reported}}',
        },
      },
    },
    footer: {
      /** §11.2: the status line, then the org's TalkOlog number(s) when it has one. */
      agentReady: 'Agent ready · texts & calls',
      sitelogNumbers: 'TalkOlog numbers',
      profile: 'Profile',
      signOut: 'Sign out',
      userMenu: 'Account menu',
    },
    demo: {
      chip: 'Demo',
      chipTitle: 'Demo mode — in-memory sample data, nothing is saved',
      menuTitle: 'Demo controls',
      simulate: 'Simulate report',
      simulateHint: 'Elm St. files a late report',
      simulated: 'Priya’s report just arrived — Elm St. flipped to Filed (late).',
      alreadyFiled: 'Elm St. already filed today.',
      resetHint: 'Sign out and in again to reset the data',
      switchUser: 'Switch demo user',
      users: {
        admin: 'Lena Ortiz · admin',
        pm: 'Sam Kowalski · project manager',
        ops: 'Ravi Patel · operations',
        accounts: 'Accounts · payroll',
      },
    },
    offline: 'You’re offline — changes will retry.',
    backOnline: 'Back online.',
  },

  common: {
    save: 'Save',
    saveChanges: 'Save changes',
    cancel: 'Cancel',
    close: 'Close',
    confirm: 'Confirm',
    retry: 'Retry',
    back: 'Back',
    next: 'Next',
    done: 'Done',
    edit: 'Edit',
    delete: 'Delete',
    remove: 'Remove',
    add: 'Add',
    open: 'Open',
    view: 'View',
    viewAll: 'View all',
    showMore: 'Show more',
    showLess: 'Show less',
    loading: 'Loading…',
    saving: 'Saving…',
    search: 'Search',
    filter: 'Filter',
    all: 'All',
    none: 'None',
    yes: 'Yes',
    no: 'No',
    today: 'Today',
    total: 'Total',
    hoursUnit: 'h',
    workersUnit: 'workers',
    optional: 'optional',
    required: 'required',
    comingSoon: 'Coming soon',
    notAvailable: '—',
    previous: 'Previous',
    nextPage: 'Next page',
    previousPage: 'Previous page',
    page: (n: number, total: number) => `Page ${n} of ${total}`,
    results: (n: number) => `${plural(n, 'result')}`,
    selected: (n: number) => `${n} selected`,
    moreActions: 'More actions',
    copied: 'Copied',
    on: 'On',
    off: 'Off',
    dismiss: 'Dismiss',
    learnMore: 'Learn more',
    call: (name: string) => `Call ${name}`,
    message: (name: string) => `Message ${name}`,
    openWhatsApp: 'Opens WhatsApp from your own phone number',
    unsavedChanges: 'You have unsaved changes. Leave without saving?',
    perMonth: '/ month',
    noSetupFee: 'no setup fee',
    hours: (n: string) => `${n} h`,
    chooseFile: 'Choose file',
    noFile: 'No file chosen',
    emailInvalid: 'Enter a valid email.',
    demoOnly: 'Demo mode: simulated in memory.',
  },

  /**
   * §11.3 `ChannelIcon` (W1-16): how a site reports. The visible label is short; the screen reader
   * hears what it means (spec: "Reported by text link" / "Reported by call").
   */
  channel: {
    link: 'Link',
    call: 'Call',
    office: 'Typed',
    reported: {
      link: 'Reported by text link',
      call: 'Reported by call',
      office: 'Typed in the panel',
    },
    prefers: {
      link: 'Reports by text link',
      call: 'Reports by call',
      office: 'Typed in the panel',
    },
  },

  chips: {
    filed: 'Filed',
    awaiting: 'Awaiting',
    missing: 'Missing',
    needs_review: 'Needs review',
    weather_day: 'Weather day',
    no_work: 'No work',
    signed_off: 'Signed off',
    pending: 'Awaiting',
    late: 'Late',
    lateAt: (time: string) => `Late ${time}`,
    lateAtTime: 'Late {{time}}',
    paused: 'Paused',
    active: 'Active',
    archived: 'Archived',
    unconfirmed: 'Unconfirmed',
    confirmed: 'Confirmed',
    edited: (v: number) => `Edited v${v}`,
    asked: (time: string) => `Asked ${time}`,
    confirmedByReply: 'Confirmed by reply',
    inviteSent: 'Invite sent',
    /** §11 "Opted out of texts": a STOP, or the SMS provider's own opt-out list (Twilio 21610). */
    optedOut: 'Opted out of texts',
    disabled: 'Disabled',
    open: 'Open',
    queried: 'Queried',
    resolved: 'Resolved',
    paid: 'Paid',
    pastDue: 'Past due',
    ready: 'Ready',
    building: 'Building',
    queued: 'Queued',
    failed: 'Failed',
    autoLearned: 'Auto-learned',
    builtIn: 'Built-in',
    rejected: 'Rejected',
    inDelayLog: (hours: string) => `In delay log · ${hours}`,
  },

  /** P4 streak bar (W1-18: read through `t()`, Turkish in `UI_RESOURCES.tr.panel.streak`). */
  streak: {
    legend: {
      on_time: 'On time',
      late: 'Late',
      weather_day: 'Weather day',
      missing: 'Missing',
      pending: 'Pending',
    },
    summary: '{{onTime}} of {{total}} on time',
    cellLabel: '{{date}}: {{status}}',
  },

  /** P5 calendar (W1-18). Weekday and month names come from `@sitelog/shared/format.ts`. */
  calendar: {
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    weekend: 'Weekend',
    today: 'Today',
    noReport: 'No report',
    crew: '{{count}} crew',
    /** The unit after a day's crew count (Web-Site: "22 crew"); hidden on narrow cells. */
    crewUnit: 'crew',
    late: 'Late',
    openReport: 'Open report for {{date}}',
    weatherAffected: 'Weather affected',
    /** Web-Site, verbatim. */
    legend: 'Weekends greyed · tap a day to open its report · cloud = weather delay logged',
    /** P5 "channel dot per day": how that day's report came in. */
    channel: {
      link: 'Text link',
      call: 'Call',
      office: 'Typed',
    },
    channelLegend: 'How each report came in',
  },

  /** P9 / F20 evidence chips (W3-07): *Voice note ts* or *Call ts* · Photos n · Weather data. */
  evidence: {
    voiceNote: 'Voice note {{ts}}',
    call: 'Call {{ts}}',
    /** The chip opens the report's player at that second (P7). */
    openVoiceNote: 'Open report {{no}} at voice note {{ts}}',
    openCall: 'Open report {{no}} at call {{ts}}',
    photos_one: '{{count}} photo',
    photos_other: '{{count}} photos',
    weatherData: 'Weather data',
    photoRequested: 'Photo requested',
    photoMissing: 'No photo',
    audioMissing: 'No audio',
    weatherMissing: 'No weather data',
    claimReady: 'Claim-ready',
  },

  source: {
    audio: (ts: string) => `audio ${ts}`,
    reply: (time: string) => `reply ${time}`,
    call: (ts: string) => `call ${ts}`,
    editedBy: (name: string) => `edited by ${name}`,
    weatherApi: 'weather data',
    seekTo: (ts: string) => `Play voice note from ${ts}`,
    openMessage: 'Open message',
    openHistory: 'Open version history',
    /** §11.3 `SourceLink` (P7): "audio 0:41" · "call 2:15" · "answer 17:41" · "edited by Lena". */
    links: {
      audio: 'audio {{ts}}',
      call: 'call {{ts}}',
      answer: 'answer {{time}}',
      reply: 'reply {{time}}',
      editedBy: 'edited by {{name}}',
      editedByOffice: 'edited by the office',
      /** Spec §19 Q80 (W2-10): the reporter changed the value on the phone (a stepper, typed). */
      reporterEdit: 'changed on the phone {{time}}',
      weather: 'weather data',
      playVoiceNote: 'Play voice note from {{ts}}',
      playCall: 'Play call from {{ts}}',
      showAnswer: 'Show the answer in Activity',
      /** §19 Q93 (W3-05): an answer said on the recorder plays its clip from the answer. */
      playAnswer: 'Play the spoken answer and show it in Activity',
      openHistory: 'Open version history',
    },
  },

  /** §11.3 `MediaPlayer`: a voice note or a call recording. */
  player: {
    play: 'Play voice note',
    pause: 'Pause voice note',
    playCall: 'Play call recording',
    pauseCall: 'Pause call recording',
    label: 'Voice note',
    labelCall: 'Call recording',
    languages: '{{from}} → {{to}}',
    noAudio: 'Audio not available',
    demoAudio: 'Demo playback (silent)',
    /** W3-05: the demo's synthetic call recordings. */
    demoTones: 'Demo recording: tones, no speech',
    speed: 'Playback speed',
    speedValue: '{{rate}}×',
    /** §11.3 (W3-05): a call's two lanes and the speaker filter. */
    laneReporter: 'Site manager',
    laneAgent: 'TalkOlog assistant',
    speakerFilter: 'Speaker',
    filter: {
      both: 'Both',
      reporter: 'Site manager',
      agent: 'Assistant',
    },
    /** §19 Q93: a spoken answer's clip in Activity. */
    answerClip: 'Spoken answer',
  },

  table: {
    empty: 'Nothing to show yet.',
    sortBy: (col: string) => `Sort by ${col}`,
    scrollHint: 'Scroll sideways to see more columns',
  },

  states: {
    loading: (thing: string) => `Loading ${thing}…`,
    error: 'Couldn’t load {{thing}}.',
    errorId: 'Error ID {{id}}',
    retry: 'Retry',
    noAccessTitle: 'No access',
    noAccess: 'You don’t have access to {{page}}. Ask an admin.',
    goToHours: 'Go to crew hours',
    goHome: 'Go to overview',
    notFoundTitle: 'Page not found',
    notFound: 'That page doesn’t exist, or the link was mistyped.',
    comingNextTitle: 'Coming next',
    comingNext: 'This page is being built and will be ready soon.',
    offline: 'You’re offline — changes will retry.',
    reviewReasons: {
      title: (n: number) => `${plural(n, 'reason')} to review`,
      unknown_company: (name: string) => `Unknown subcontractor “${name}”`,
      low_confidence: (path: string) => `Low confidence: ${path}`,
      unanswered: (field: string) => `Unanswered question: ${field}`,
      unconfirmed: 'Filed without confirmation from the site manager',
      generic: (reason: string) => reason,
      addAsSub: 'Add as subcontractor',
      mapTo: 'Map to existing',
      resolve: 'Resolve',
    },
  },

  login: {
    /** P1 and the Web-Login artboard. */
    headline: 'End-of-day reports, spoken in three minutes.',
    bullets: {
      link: 'Site managers tap a text link or take a call — nothing to install.',
      pdf: 'A formatted PDF daily report lands here, ready to file.',
      trace: 'Sub hours and weather delays, each traceable to the recording.',
    },
    tagline: 'Talk. It’s logged.',
    title: 'Sign in to the office panel',
    subtitle: 'We’ll email you a one-time link. No password to remember.',
    emailLabel: 'Work email',
    emailPlaceholder: 'you@company.com',
    emailInvalid: 'Enter a valid work email.',
    submit: 'Email me a sign-in link',
    sending: 'Sending…',
    or: 'or',
    sso: 'Continue with company SSO',
    ssoSoon: 'Coming soon',
    managersNoteLead: 'Site managers don’t sign in.',
    managersNote: 'They report by text link or phone call.',
    footer: 'New here?',
    footerCta: 'Set up your company',
    footerSuffix: '— no setup fee.',
    sentTitle: 'Check your inbox',
    sent: 'We sent a sign-in link to {{email}}. It’s valid for 1 hour.',
    resend: 'Resend link',
    resendIn: 'Resend in {{seconds}} s',
    useAnother: 'Use a different email',
    expired: 'That link has expired. Request a new one below.',
    authError: 'Sign-in didn’t work. Try again.',
    rateLimited: 'Too many sign-in emails. Wait a minute and try again.',
    demoHint: 'Demo mode: any email signs you in as Lena Ortiz (admin).',
    demoHintAs: 'Demo mode: you’ll sign in as {{name}}.',
    callbackTitle: 'Signing you in…',
    callbackBody: 'One moment while we check your link.',
    accountThing: 'your companies',
    signOut: 'Sign out',
  },

  /** The error boundary around the whole panel (`src/app/AppErrorBoundary.tsx`). */
  errorBoundary: {
    title: 'Something went wrong',
    body: 'TalkOlog hit an unexpected error. Try again, or sign out and sign back in.',
    retry: 'Try again',
    signOut: 'Sign out',
    reported: 'The TalkOlog team has been told about it.',
    errorId: 'Error ID {{id}}',
  },

  /**
   * `/dev/phone` · Demo phone (§11.1, W1-08): the texts of the `console` SMS driver, one thread
   * per reporter, and the emails of the console email provider.
   */
  devPhone: {
    subtitle: 'Every text TalkOlog sends through the console driver, one thread per reporter.',
    tabsLabel: 'Demo phone views',
    tabTexts: 'Texts',
    tabEmail: 'Email',
    reportersLabel: 'Reporters',
    textsCount_one: '{{count}} text',
    textsCount_other: '{{count}} texts',
    noTexts: 'No texts yet',
    pickReporter: 'Pick a reporter to see their texts.',
    back: 'All reporters',
    threadLabel: 'Texts with {{name}}',
    threadEmpty: 'No texts with {{name}} yet.',
    fromTalkOlog: 'TalkOlog',
    opensInNewTab: '(opens in a new tab)',
    copyKey: 'Message',
    /** W2-14: the channel of each message (a push to the installed app, or a text). */
    channelLabel: 'Channel',
    channels: {
      sms: 'Text',
      push: 'Push notification',
      whatsapp: 'WhatsApp',
      /** W3-17: a demo org's outbound call rings here (console telephony, spec §19 Q132). */
      call: 'Call',
    },
    /** W3-17: what an outbound call of TalkOlog's did on the Demo phone. */
    call: {
      ringing: 'TalkOlog is calling: ringing',
      answered: 'On a call with TalkOlog',
      ended: 'Call with TalkOlog ended',
      missed: 'Missed call from TalkOlog',
      busy: 'Call from TalkOlog: busy',
      voicemail: 'Call from TalkOlog reached voicemail: no message left',
      failed: 'Call from TalkOlog could not be placed',
    },
    callPurpose: {
      reminder: 'Reminder call',
      callback: 'Callback',
      daily_report: 'Call from the office',
    },
    statusLabel: 'Status',
    status: {
      queued: 'Sending',
      sent: 'Sent',
      delivered: 'Delivered',
      read: 'Read',
      received: 'Received',
      failed: 'Failed',
      undeliverable: 'Not delivered',
    },
    replyLabel: 'Reply as {{name}}',
    /** W2-16: a WhatsApp reply (the pack lists WhatsApp). */
    replyLabelWhatsapp: 'Reply on WhatsApp as {{name}}',
    replyChannel: 'Reply by',
    replyPlaceholder: 'Type a text, e.g. HELP or STOP',
    send: 'Send',
    replyFailed: 'The reply was not sent.',
    oneWay:
      'Replies are off: the {{code}} country pack has one-way SMS, so a reporter cannot text TalkOlog.',
    oneWayWhatsapp:
      'The {{code}} country pack has one-way SMS: a reporter replies to TalkOlog on WhatsApp.',
    maskedNote: 'This is not a demo organisation: link codes are masked and nothing can be typed.',
    notDemoTitle: 'The Demo phone is for demo organisations',
    notDemoBody:
      'This organisation’s texts go to real phones. Switch to a demo organisation to use the Demo phone.',
    emptyTitle: 'No reporters yet',
    emptyBody: 'Invite a reporter on Team & numbers: the welcome text appears here.',
    emailLabel: 'Emails',
    emailEmpty:
      'No emails yet. While the worker uses the console email provider, every email it would send appears here.',
    emailTo: 'To {{to}}',
    attachments: 'Attachments',
    attachmentSize: '{{name}} · {{size}} KB',
    thing: 'the Demo phone',
    threadThing: 'the texts',
    emailThing: 'the emails',
  },

  /**
   * P3 Overview and the Web-Overview artboard (W1-16): plain strings for `t()` (EN and TR), so the
   * page speaks the panel's language. `noSites`, `noSitesAskAdmin` and `connectFirstSite` are also
   * read by Sites, Reports and Settings.
   */
  overview: {
    title_one: 'Today across {{count}} site',
    title_other: 'Today across {{count}} sites',
    pdfs: 'Today’s PDFs',
    pdfsDemo: 'Demo mode: PDFs are rendered by the worker from each filed report.',
    pdfsNone: 'No PDFs yet today.',
    kpisLabel: 'Today in numbers',
    kpis: {
      reportsIn: 'Reports in',
      crew: 'Crew on site',
      crewUnit: 'workers',
      hours: 'Labor hours logged',
      weather: 'Weather-affected sites',
      weatherLink: 'Added to delay log',
      segments: {
        filed: 'Filed',
        weather: 'Weather day',
        missing: 'Missing',
        awaiting: 'Awaiting',
      },
    },
    sitesToday: 'Sites today',
    allSites: 'All sites',
    columns: {
      site: 'Site',
      status: 'Status',
      channel: 'Channel',
      inAt: 'In at',
      crew: 'Crew',
      hours: 'Hours',
      weather: 'Weather',
    },
    weatherNone: 'Dry, no impact',
    weatherFrom: 'from {{time}}',
    lostHours: '{{hours}} lost',
    remindersSent_one: '{{count}} reminder sent',
    remindersSent_other: '{{count}} reminders sent',
    needsAttention: 'Needs attention',
    allClear: 'All clear',
    allClearBody: 'Every site has reported and nothing is waiting on you.',
    nothingYet: 'Nothing needs you yet',
    nothingYetBody_one: '{{count}} site still to report today.',
    nothingYetBody_other: '{{count}} sites still to report today.',
    emptyBeforeReports: 'Reports usually arrive after {{deadline}}. Reminders go out at {{r1}}.',
    emptyBeforeReportsNoReminders: 'Reports usually arrive after {{deadline}}.',
    emptyNoReporter: 'No site manager reports yet',
    emptyNoReporterBody:
      'Type today’s report in the panel until a site manager is invited to report by text link or call.',
    noSitesTitle: 'No sites yet',
    noSites: 'Connect your first site, then file its daily reports from Reports → New report.',
    noSitesAskAdmin: 'An admin connects sites. Ask an admin to connect the first one.',
    connectFirstSite: 'Connect your first site',
    allPausedTitle: 'All sites are paused',
    allPaused: 'Resume a site from its settings and daily reports start again.',
    allPausedAskAdmin:
      'An admin resumes sites. Ask an admin to resume one and daily reports start again.',
    openSites: 'Open sites',
    /** D29: a demo organisation's texts all land on the Demo phone (§4.5, §11.1). */
    demoPhoneCard: {
      eyebrow: 'Demo',
      title: 'Every text lands on the Demo phone',
      body: 'This is a demo organisation: welcome texts, today’s links and reminders go to the Demo phone, not to real phones. Open a link there to report as a site manager, then come back and watch this page update.',
      cta: 'Open the Demo phone',
    },
    attention: {
      /** The kind tag on each card (Web-Overview: MISSING · NEEDS REVIEW · INVOICE CHECK). */
      labels: {
        missing: 'Missing',
        needs_review: 'Needs review',
        invoice_gap: 'Invoice check',
        unknown_contact: 'Unknown contact',
        glossary_review: 'Glossary',
        undeliverable: 'Not delivered',
      },
      missing: {
        title: '{{site}} — no report',
        body_one: '{{first}} didn’t answer {{count}} reminder.',
        body_other: '{{first}} didn’t answer {{count}} reminders.',
        bodyNoReminders: 'No report from {{first}} by the deadline.',
        /** Web-Overview: the §19 Q55 `missing_report` notice of that day. */
        alerted: 'Office alerted at {{time}}.',
        call: 'Call {{first}}',
        textLink: 'Text today’s link',
        markNoWork: 'Mark no work',
      },
      needs_review: {
        title: '{{site}} needs review',
        titleUnknownCompany: '{{site}} — unknown sub “{{name}}”',
        unknownCompany_one: '{{count}} worker mentioned, not in your subcontractor list.',
        unknownCompany_other: '{{count}} workers mentioned, not in your subcontractor list.',
        review: 'Review report',
      },
      /** reports.review_reasons (§10.5, §10.9) as the Overview lists them. */
      reasons: {
        unknown_company: 'Unknown subcontractor “{{name}}”',
        low_confidence: 'Low confidence: {{field}}',
        unanswered: 'Unanswered question: {{field}}',
        unconfirmed: 'Filed without confirmation from the site manager',
        missing: 'Missing: {{field}}',
        no_photo: 'No photo arrived after the call',
        call_mismatch: 'The call recording differs from the confirmed report: {{field}}',
        /** §19 Q123, Q153 (W3-11): the post-call reconcile could not run. */
        call_unreconciled: 'The call recording could not be checked against the report',
        other: '{{reason}}',
      },
      invoice_gap: {
        title: '{{company}}: {{invoiced}} billed',
        bodyOver: 'Daily reports this week show {{reported}} — {{gap}} gap.',
        bodyUnder: 'Daily reports this week show {{reported}} — {{gap}} more than billed.',
        compare: 'Compare hours',
      },
      unknown_contact: {
        titleCall: 'Unknown caller',
        titleText: 'Unknown number texted',
        titleWhatsapp: 'Unknown number tried to report',
        body: '{{phone}} · “{{snippet}}”',
        bodyNoSnippet: '{{phone}}',
        link: 'Link number',
      },
      glossary_review: {
        title_one: '{{count}} glossary term to review',
        title_other: '{{count}} glossary terms to review',
        body: '{{terms}}',
        review: 'Review terms',
      },
      undeliverable: {
        title: 'Text to {{name}} not delivered',
        body: 'The text sent at {{time}} did not reach their phone.',
        call: 'Call {{first}}',
        checkNumber: 'Check the number',
      },
    },
    textLink: {
      sent: 'Today’s link texted to {{first}}.',
      notSent: {
        no_consent: '{{first}} has not agreed to texts, so nothing was sent.',
        opted_out: '{{first}} opted out of texts, so nothing was sent.',
        no_channel: 'No message channel can reach {{first}}, so nothing was sent.',
        console_real_org:
          'Texts are not live for this organisation’s country yet, so nothing reached {{first}}.',
      },
      /** api-text-daily-link's 409 `details.code`: the site has no one to text. */
      failed: {
        no_reporter: 'No site manager is linked to this site. Add one on Team & numbers.',
        no_active_reporter:
          'No active site manager on this site: the linked ones are disabled, opted out or have not accepted their invite yet.',
        /** W4-03: Team's row menu names the reporter. */
        reporter_not_linked: 'This reporter is no longer linked to that site.',
        reporter_not_active:
          'This reporter is not active yet: disabled, opted out of texts or still to accept the invite.',
      },
    },
    markNoWorkDialog: {
      title: 'Mark no work at {{site}}?',
      body: '{{date}} will be recorded as a day with no work. The site manager can still send a late report, which replaces this.',
      confirm: 'Mark no work',
      done: '{{site}} marked as no work.',
    },
    liveUpdates: 'Live updates on',
  },

  /** P4 Sites (W1-18): read through `t()`, Turkish in `UI_RESOURCES.tr.panel.sites`. */
  sites: {
    title: 'Sites',
    /** Web-Sites, verbatim (spec P4). */
    subtitle: '{{count}} active · each reports daily by text link or call',
    addSite: 'Add site',
    tabs: { all: 'All', attention: 'Attention', paused: 'Paused' },
    legend: 'Last 14 workdays',
    lastWorkdays: 'Last 14 workdays · {{onTime}} on time',
    crewToday: '{{count}} crew',
    /** No report filed today, so no crew count yet. */
    crewTodayNone: '— crew',
    weatherDays_one: '{{count}} weather day in {{month}}',
    weatherDays_other: '{{count}} weather days in {{month}}',
    weatherDaysNone: 'No weather days in {{month}}',
    connectAnother: 'Connect another site',
    /** Web-Sites (US): "$399 / month · no setup fee"; the price comes from the org's country. */
    connectAnotherPrice: '{{price}} / month · no setup fee',
    emptyAttention: 'No site needs attention right now.',
    emptyPaused: 'No paused sites.',
    empty: 'No sites yet',
    emptyBody: 'Connect your first site, then file its daily reports from Reports → New report.',
    emptyBodyAskAdmin: 'An admin connects sites. Ask an admin to connect the first one.',
    connectFirstSite: 'Connect your first site',
    openSite: 'Open {{name}}',
    cardLabel: 'Site card: {{name}}',
    pausedSince: 'Paused since {{date}}',
    paused: 'Paused',
    reporter: 'Site manager',
    /** §2.3 `contact_pref`, beside the site manager (P4 "preference icon", P5 "Prefers text link"). */
    prefers: {
      link: 'Prefers text link',
      call: 'Prefers call',
    },
    filterLabel: 'Filter sites',
  },

  /** P5 Site detail (W1-18): read through `t()`, Turkish in `UI_RESOURCES.tr.panel.siteDetail`. */
  siteDetail: {
    breadcrumb: 'Sites',
    breadcrumbLabel: 'Breadcrumb',
    sectionsLabel: 'Site sections',
    tabs: {
      reports: 'Daily reports',
      crew: 'Crew & subs',
      weather: 'Weather & delays',
      photos: 'Photos',
    },
    siteSettings: 'Site settings',
    /** P5 header actions (Web-Site: "Call Marco"). */
    call: 'Call {{first}}',
    callHint: 'Calls {{phone}} from your phone',
    textLink: 'Text today’s link',
    newReport: 'New report',
    latest: 'Latest · {{date}}',
    openReport: 'Open report',
    thisMonth: 'This month so far',
    crewToday: 'Crew today, by company',
    stats: {
      reports: 'Reports',
      reportsOf: '{{count}} of {{workdays}} workdays',
      onTime: 'On time',
      weatherHours: 'Weather hours',
      laborHours: 'Labor hours',
      avgCrew: 'Avg crew',
    },
    columns: { company: 'Company', trade: 'Trade · area', workers: 'Workers', hours: 'Hours' },
    total: 'Total',
    viewAll: 'View all',
    /** Web-Site: "Report deadline 17:30 CT". */
    deadline: 'Report deadline {{time}} {{zone}}',
    pauseSite: 'Pause site',
    resumeSite: 'Resume site',
    /** Web-Site: "Prefers text link · speaks ES & EN". */
    speaks: 'speaks {{languages}}',
    languagesAnd: '{{first}} & {{last}}',
    backup: 'Backup',
    primary: 'Site manager',
    latestEmpty: 'No report filed yet.',
    summaryEmpty: 'No summary.',
    latestWeatherDay: 'Weather day',
    trio: { crew: 'Crew', hours: 'Hours', weatherHours: 'Weather hours' },
    crewTodayEmpty: 'No crew reported today.',
    /** P5 crew-by-company badges ("CONFIRMED BY ANSWER", "EDITED V2"). */
    badges: {
      edited: 'Edited v{{version}}',
      asked: 'Asked {{time}}',
      confirmedByAnswer: 'Confirmed by answer',
      unconfirmed: 'Unconfirmed',
    },
    crewTab: {
      title: 'Crew & subs this week',
      subtitle: 'Hours reported at this site · {{week}}',
      openHours: 'Open crew hours',
      previousWeek: 'Previous week',
      nextWeek: 'Next week',
      empty: 'No hours reported at this site this week.',
      columns: { company: 'Company', reported: 'Reported' },
      thing: 'crew hours',
    },
    weatherTab: {
      title: 'Delay log',
      subtitle: 'Lost hours at this site · {{month}}',
      openWeather: 'Open weather & delays',
      empty: 'No weather delays this month.',
      lostLabel: 'Lost',
      fullDay: 'Full day',
      period: '{{start}}–{{end}}',
      periodFrom: 'from {{start}}',
      periodUntil: 'until {{end}}',
      observed: 'Observed precip {{precip}}',
      thing: 'weather & delays',
    },
    photosTab: {
      title_one: '{{count}} photo',
      title_other: '{{count}} photos',
      dateFilter: 'Date',
      allDates: 'All dates',
      empty:
        'No photos yet. Site managers send them with the voice note; the office adds them to a report with Edit.',
      open: 'Open photo: {{caption}}',
      noPreview: 'Preview not available',
      openReport: 'Open report {{no}}',
      caption: '{{date}} · {{time}}',
      taken: 'Taken {{time}}',
    },
    settings: {
      title: 'Site settings',
      name: 'Site name',
      projectCode: 'Project code',
      address: 'Address',
      addressHint: 'Used to pull local weather data for the delay log.',
      lat: 'Latitude',
      lng: 'Longitude',
      timezone: 'Time zone',
      deadline: 'Deadline override',
      deadlineHint: 'Leave empty to use the company default ({{org}}).',
      workingHours: 'Working hours',
      workingHoursHint: 'e.g. 07:00–15:30',
      reporters: 'Reporters',
      reportersHint: 'Reporters are managed in Team & numbers.',
      noReporters: 'No reporter linked yet.',
      inviteSent: 'Invite sent',
      recipients: 'PDF recipients',
      recipientsHint: 'One email per line or separated by commas.',
      recipientsInvalid: '“{{email}}” is not a valid email.',
      nameRequired: 'Site name is required.',
      codeRequired: 'Project code is required.',
      timeInvalid: 'Use HH:MM (24 h).',
      latLngInvalid: 'Enter a number.',
      pauseTitle: 'Pause {{site}}?',
      pauseBody:
        'Reports from this site stop and it won’t be billed from the next cycle. You can resume any time.',
      pauseConfirm: 'Pause site',
      resumeTitle: 'Resume {{site}}?',
      resumeBody: 'Daily reports and reminders start again from the next working day.',
      resumeConfirm: 'Resume site',
      pausedNote: 'Paused since {{date}}',
      cancel: 'Cancel',
      save: 'Save changes',
    },
  },

  reports: {
    title: 'Reports',
    subtitle: 'Every daily report, searchable down to the spoken word',
    exportZip: 'Export PDFs (.zip)',
    newReport: 'New report',
    filters: {
      site: 'Site',
      allSites: 'All sites',
      dates: 'Dates',
      from: 'From',
      to: 'To',
      status: 'Status',
      anyStatus: 'Any status',
      channel: 'Channel',
      weatherDelay: 'Has weather delay',
      edited: 'Edited',
      late: 'Late',
      incident: 'Incident',
      search: 'Search report text and transcripts',
      clear: 'Clear filters',
    },
    /** P6 Channel filter (Any · Link · Call). */
    channels: {
      any: 'Any',
      link: 'Link',
      call: 'Call',
    },
    columns: {
      date: 'Date',
      reportNo: 'Report no.',
      site: 'Site',
      crew: 'Crew',
      hours: 'Hours',
      flags: 'Flags',
      status: 'Status',
      pdf: 'PDF',
    },
    /** P6 flags, worded from `ReportFlag` (assemble.ts `reportFlags`). */
    flags: {
      rain: 'Rain {{hours}}',
      version: 'v{{version}}',
      unknownSub: 'Unknown sub',
      late: 'Late {{time}}',
      incident: 'Incident',
      weatherDay: 'Weather day',
      noPhoto: 'No photo',
    },
    downloadPdf: 'Download PDF {{no}}',
    selectRow: 'Select {{no}}',
    selectAll: 'Select all on this page',
    count_one: '{{count}} report',
    count_other: '{{count}} reports',
    countSelected_one: '{{count}} report · {{selected}} selected',
    countSelected_other: '{{count}} reports · {{selected}} selected',
    buildPack: 'Build EOT evidence pack from these',
    buildPackHint: '{{hours}} lost to weather in this set',
    empty: 'No reports match these filters.',
    emptyHint: 'Try widening the dates or clearing a toggle.',
    emptyNone: 'No reports yet',
    emptyNoneBody: 'Type a site’s daily report and it is filed here, with its PDF.',
    emptyNoSitesBody: 'Add your first site, then file its daily reports here.',
    emptyNoSitesAskAdmin:
      'An admin adds sites. Ask an admin to add the first one, then file its daily reports here.',
    connectFirstSite: 'Connect your first site',
    exportDemo_one:
      'Demo mode: a zip with {{count}} PDF would download here. PDFs are rendered by the worker.',
    exportDemo_other:
      'Demo mode: a zip with {{count}} PDFs would download here. PDFs are rendered by the worker.',
    exportNone: 'No reports to export. Widen the filters or select rows.',
    statuses: {
      all: 'Any status',
      filed: 'Filed',
      needs_review: 'Needs review',
      signed_off: 'Signed off',
      weather_day: 'Weather day',
    },
    filterBar: 'Filters',
    toggles: 'Quick filters',
    manager: 'by {{name}}',
    pdfNotReady: 'PDF not ready yet',
    pdfNotStored: 'No PDF stored yet. It is made when you open it.',
    pagination: 'Pagination',
    showing: '{{from}}–{{to}} of {{total}}',
    page: 'Page {{page}} of {{total}}',
    previousPage: 'Previous page',
    nextPage: 'Next page',
    thing: 'reports',
  },

  newReport: {
    title: 'New report',
    subtitle: 'Type the day’s report for a site. It is filed as soon as you save it.',
    breadcrumb: 'Reports',
    site: 'Site',
    pickSite: 'Choose a site',
    date: 'Date',
    noSites: 'Add a site before writing a report.',
    future: 'The date can’t be in the future.',
    empty: 'Write a summary or add at least one row before filing.',
    submit: 'File report',
    cancel: 'Cancel',
    filed: '{{no}} filed.',
    exists:
      '{{no}} already covers this site and date, so this report was not filed. What you typed is still here: open {{no}} and use Edit to add to it, or pick another site or date.',
    openExisting: 'Open {{no}}',
    restored:
      'Restored the report you were typing in this tab. Photos are not kept: add them again.',
    startOver: 'Start over',
    pdfLater: 'The report is filed. The PDF could not be made yet; open the report and press PDF.',
  },

  report: {
    breadcrumb: 'Reports',
    eyebrow: 'Daily report · {{no}}',
    version: 'v{{version}}',
    versionCurrent: 'v{{version}} · current',
    versionSwitcher: 'Version',
    previous: 'Previous report',
    next: 'Next report',
    thing: 'report',
    /**
     * P7 provenance line, joined with " · ": "Reported by Marco Ruiz · voice note via text link at
     * 17:38 · confirmed on the recorder 17:43 · edited 20:10" / "Reported by Aisha Bello · phone
     * call 18:41 (2 min 05 s) · confirmed by voice". WhatsApp-era reports keep their wording.
     */
    provenance: {
      reportedBy: 'Reported by {{name}}',
      viaLink: 'voice note via text link at {{time}}',
      viaCall: 'phone call {{time}} ({{duration}})',
      viaCallNoLength: 'phone call {{time}}',
      /** W3-05: TalkOlog called the site manager (a reminder or call-back call, M8). */
      viaCallOut: 'call from TalkOlog {{time}} ({{duration}})',
      viaCallOutNoLength: 'call from TalkOlog {{time}}',
      /** W3-05: Talk now in the recorder (`calls.transport 'web'`). */
      viaBrowserCall: 'browser call {{time}} ({{duration}})',
      viaBrowserCallNoLength: 'browser call {{time}}',
      /** §9.4 rule 9: the call switched to the simple recording (outcome `fallback_recording`). */
      fallbackRecording: 'simple recording mode',
      viaText: 'by text at {{time}}',
      viaWhatsApp: 'Reported by {{name}} by WhatsApp voice at {{time}}',
      confirmed: 'confirmed {{time}}',
      confirmedOnRecorder: 'confirmed on the recorder {{time}}',
      confirmedByVoice: 'confirmed by voice',
      confirmedByText: 'confirmed by text {{time}}',
      edited: 'edited {{time}}',
      editedOnCall: 'edited on a call {{time}}',
      editedByReply: 'edited by reply {{time}}',
      editedBy: 'edited by {{name}} {{time}}',
      signedOff: 'signed off by {{name}} {{time}}',
      filedWithoutConfirmation: 'filed without confirmation',
      enteredBy: 'Entered by {{name}} in TalkOlog at {{time}}',
    },
    actions: {
      edit: 'Edit',
      pdf: 'PDF',
      signOff: 'Sign off',
      signedOffBy: 'Signed off by {{name}}',
      undo: 'Undo',
      save: 'Save new version',
      cancel: 'Cancel',
    },
    signOffDialog: {
      title: 'Sign off {{no}} v{{version}}?',
      body: 'Sign-off is recorded in the audit log with your name and the time. You can undo within 10 minutes.',
      confirm: 'Sign off',
      cancel: 'Cancel',
    },
    sections: {
      summary: 'Summary',
      weather: 'Weather & impact',
      manpower: 'Manpower',
      work: 'Work performed',
      deliveries: 'Deliveries',
      safety: 'Safety',
      nextWorkday: 'Next workday',
      photos: 'Photos',
      issues: 'Issues',
      equipment: 'Equipment',
    },
    weather: {
      observed: 'Observed',
      precip: 'Precip',
      temp: 'Temp',
      wind: 'Wind',
      gusts: 'Gusts',
      impact: 'Impact on work',
      attribution: 'Weather data: Open-Meteo',
      none: 'No weather impact reported.',
      fullDay: 'Full day',
    },
    manpower: {
      company: 'Company',
      tradeArea: 'Trade · area',
      workers: 'Workers',
      hours: 'Hours',
      source: 'Source',
      total: 'Total',
      addRow: 'Add company',
      removeRow: 'Remove row',
      addNewCompany: 'Add new…',
      hoursPerWorker: 'h / worker',
      editorTotal: 'Total: {{workers}} workers · {{hours}}',
    },
    work: {
      area: 'Area',
      activity: 'Activity',
      status: 'Status',
      heardAs: 'heard “{{words}}”',
    },
    workStatus: {
      complete: 'Complete',
      in_progress: 'In progress',
      started: 'Started',
      stopped: 'Stopped',
      planned: 'Planned',
    },
    safety: {
      toolboxTalk: 'Toolbox talk: {{topic}}',
      noIncidents: 'No incidents',
      incidents_one: '{{count}} incident',
      incidents_other: '{{count}} incidents',
      notStated: 'Not stated',
      visitors: 'Visitors: {{names}}',
    },
    /**
     * The words of a transcript excerpt's mapped facts ("Apex Concrete · 6 workers × 8 = 48 h"):
     * report content, so in the report's language whatever the panel's (assemble.ts `factWords`).
     */
    facts: {
      workers: '{{count}} workers',
      lost: '{{hours}} lost',
      toolboxTalk: 'Toolbox talk: {{topic}}',
      noIncidents: 'No incidents',
      notStated: 'Not stated',
    },
    aside: {
      source: 'Source',
      voiceNote: 'Voice note',
      voiceNotes: '{{count}} voice notes',
      call: 'Call',
      recordings: '{{count}} recordings',
      typed: 'Typed in TalkOlog',
      typedBy: '{{name}} typed this report in TalkOlog; there is no voice note.',
      fullTranscript: 'Full transcript',
      activity: 'Activity',
      excerpts: 'Transcript excerpts',
      clipVoiceNote: 'Voice note · {{time}}',
      clipCall: 'Call · {{time}}',
      noRecording: 'No recording with this report.',
      /** W3-05: the speaker filter hides every excerpt line of this call. */
      noExcerptsForSpeaker: 'No excerpt lines from this speaker.',
    },
    edit: {
      changeNote: 'Change note',
      changeNotePlaceholder: 'What changed and why (goes in the audit log and the email)',
      changeNoteRequired: 'A change note is required.',
    },
    diff: { edited: 'Edited v{{version}}', was: 'was {{value}}' },
    questionAsked: 'Asked {{time}}',
    confirmedByReply: 'Confirmed by reply',
    confirmedByAnswer: 'Confirmed by answer',
    photoCaption: (time: string) => `Taken ${time}`,
    photoTaken: 'Taken {{when}}',
    photoAlt: 'Site photo taken {{when}}',
    photoAltNoTime: 'Site photo',
    title: '{{weekday}} — {{site}}',
    versionLine: '{{author}} · {{time}}',
    versionLineNote: '{{author}} · {{time}} · {{note}}',
    versionAuthor: { agent: 'TalkOlog agent', reporter: 'Reply', office_user: 'Office' },
    viewingOld: 'You’re viewing version {{version}}. Version {{current}} is current.',
    viewCurrent: 'View current',
    signOffHidden: 'Sign-off is for admins and project managers.',
    undoWindow: 'Undo within 10 minutes',
    pdfPreparing: 'Preparing the PDF…',
    pdfFailed: 'The PDF could not be made. Try again.',
    pdfNotSaved: 'PDF opened, but it could not be saved to TalkOlog yet.',
    weatherDayNote: 'Weather day — no work performed.',
    summaryEmpty: 'No summary.',
    manpowerEmpty: 'No manpower reported.',
    workEmpty: 'No work items reported.',
    deliveriesEmpty: 'No deliveries.',
    nextEmpty: 'Nothing stated for the next workday.',
    issuesEmpty: 'No issues.',
    photosEmpty: 'No photos with this report.',
    photoPreview: 'Preview not available in demo mode',
    conditions: 'Conditions',
    lostHours: '{{hours}} lost',
    inDelayLog: 'In delay log · {{hours}}',
    period: (start: string | undefined, end: string | undefined) =>
      start && end ? `${start}–${end}` : start ? `from ${start}` : end ? `until ${end}` : '',
    periodRange: '{{start}}–{{end}}',
    periodFrom: 'from {{start}}',
    periodUntil: 'until {{end}}',
    quantity: '{{value}} {{unit}}',
    percent: '{{value}}%',
    manpowerSummary: '{{workers}} · {{companies}} · {{hours}}',
    workers_one: '{{count}} worker',
    workers_other: '{{count}} workers',
    companies_one: '{{count}} company',
    companies_other: '{{count}} companies',
    hoursUnknown: 'hours unknown',
    unconfirmedHint: 'Company not yet confirmed — resolve in the banner above.',
    /** Review reasons (P7 banner) of the report's `review_reasons` codes. */
    reasons: {
      title_one: '{{count}} reason to review',
      title_other: '{{count}} reasons to review',
      unknown_company: 'Unknown subcontractor “{{name}}”',
      low_confidence: 'Low confidence: {{path}}',
      unanswered: 'Unanswered question: {{field}}',
      unconfirmed: 'Filed without confirmation from the site manager',
      call_mismatch: 'The call recording and the confirmed report differ on {{path}} — check',
      /** §19 Q74 / Q123 (W3-05): `call_mismatch:<path>:<heard>:<confirmed>`, by the value's kind. */
      call_mismatch_workers_one:
        'The call recording says {{heard}} worker, the confirmed report says {{confirmed}} — check',
      call_mismatch_workers_other:
        'The call recording says {{heard}} workers, the confirmed report says {{confirmed}} — check',
      call_mismatch_hours:
        'The call recording says {{heard}} h per worker, the confirmed report says {{confirmed}} — check',
      call_mismatch_lostHours:
        'The call recording says {{heard}} h lost, the confirmed report says {{confirmed}} — check',
      call_mismatch_value:
        'The call recording says {{heard}} for {{path}}, the confirmed report says {{confirmed}} — check',
      /** The crew or weather period the values are of, before the line. */
      call_mismatch_of: '{{what}}: {{line}}',
      no_photo: 'No photo came in for this report',
      /** §19 Q123, Q153 (W3-11): the post-call reconcile could not run. */
      call_unreconciled:
        'The call recording could not be checked against the report; its seconds are live timestamps.',
      playAt: 'Play at {{ts}}',
      addAsSub: 'Add as subcontractor',
      mapTo: 'Map to existing',
    },
    activity: {
      empty: 'No activity yet.',
      agent: 'TalkOlog',
      office: 'Office',
      system: 'System',
      /** P7 Activity entries (`ActivityKind`); `{{…}}` values from the entry's params. */
      events: {
        link_opened: 'Link opened',
        clip_received: 'Voice note received · {{duration}}',
        clip_received_photos_one: 'Voice note + {{count}} photo received',
        clip_received_photos_other: 'Voice note + {{count}} photos received',
        photos_received_one: '{{count}} photo received',
        photos_received_other: '{{count}} photos received',
        report_started: 'Report started',
        extracted: 'Transcribed and fields extracted',
        question_asked: 'Question asked',
        question_answered: 'Question answered',
        question_timeout: 'Question not answered in time',
        draft_shown: 'Draft shown',
        draft_corrected: 'Draft corrected',
        confirmed: 'Confirmed',
        confirmed_web: 'Confirmed on the recorder',
        confirmed_voice: 'Confirmed by voice',
        confirmed_sms: 'Confirmed by text',
        filed: 'v{{version}} filed',
        version: 'v{{version}} saved',
        pdf_rendered: 'PDF created',
        pdf_emailed: 'PDF emailed',
        text_sent: 'Text sent · {{message}}',
        text_received: 'Text received',
        call_in: 'Call received · {{duration}}',
        call_out: 'Call placed · {{duration}}',
        call_missed: 'Call not answered',
        /** W3-05: Talk now in the recorder. */
        call_web: 'Browser call · {{duration}}',
        whatsapp_in: 'WhatsApp in · {{type}}',
        whatsapp_out: 'WhatsApp out · {{type}}',
        signed_off: 'Signed off',
        signoff_undone: 'Sign-off undone',
        signoff_reverted: 'Sign-off reverted by edit',
        needs_review: 'Flagged for review',
        company_resolved: 'Unknown company resolved',
        no_work: 'Marked no work',
        missing: 'Missing — office alerted',
        voided: 'Report voided',
        other: '{{action}}',
      },
      /** A call's outcome, after its kind ("Call received · 2 min 41 s · filed"), §6.3 `call_outcome`. */
      callOutcomes: {
        filed: 'filed',
        draft_saved: 'draft saved',
        no_work: 'no work',
        weather_day: 'weather day',
        edit_applied: 'edit applied',
        callback_requested: 'call-back requested',
        fallback_recording: 'simple recording',
        abandoned: 'ended without a report',
        not_reached: 'not reached',
      },
      /** A text's delivery, after its kind ("Text sent · Daily link · delivered"). */
      status: {
        queued: 'queued',
        sent: 'sent',
        delivered: 'delivered',
        read: 'read',
        received: 'received',
        failed: 'failed',
        undeliverable: 'undeliverable',
      },
      /** The SMS copy keys (§9.2) as the office reads them. */
      messages: {
        welcome: 'Welcome',
        verify_code: 'Device code',
        daily_link: 'Daily link',
        call_coming: 'Call coming',
        weather_headsup: 'Weather heads-up',
        reminder_1: 'Reminder',
        reminder_2: 'Second reminder',
        missed_call: 'Missed call',
        question_ready: 'Question',
        draft_ready: 'Draft ready',
        filed: 'Filed with PDF link',
        photos_link: 'Photos link',
        edit_applied: 'Edit applied',
        finish_report: 'Finish report',
        site_paused: 'Site paused',
        unknown_number: 'Unknown number',
        help: 'Help',
        stop_confirm: 'Opt-out confirmed',
        pref_call_ok: 'Prefers calls',
        pref_text_ok: 'Prefers texts',
        other: 'Message',
      },
      whatsappTypes: {
        text: 'text',
        audio: 'voice note',
        image: 'photo',
        document: 'document',
        interactive: 'reply',
        template: 'template',
        reaction: 'reaction',
        unsupported: 'message',
      },
    },
    transcript: {
      title: 'Full transcript',
      language: 'Language: {{language}}',
      none: 'No transcript for this report.',
      segments_one: '{{count}} segment',
      segments_other: '{{count}} segments',
      reporter: 'Reporter',
      agent: 'TalkOlog',
    },
    /** Lengths of clips and calls: "48 s", "2 min", "2 min 05 s". */
    duration: {
      seconds: '{{s}} s',
      minutes: '{{m}} min',
      minutesSeconds: '{{m}} min {{s}} s',
    },
    editMode: {
      title: 'Editing',
      hint: 'Edits create a new version. The original stays in history, and a new PDF goes out.',
      /** W2-11 review (§19 Q106): the report has a newer version than the one being edited. */
      versionChanged: 'This report changed. Reload to see the latest version.',
      summary: 'Summary',
      conditions: 'Conditions',
      weatherPeriods: 'Weather periods',
      addPeriod: 'Add period',
      removePeriod: 'Remove period',
      condition: 'Condition',
      start: 'Start',
      end: 'End',
      impact: 'Impact',
      lostHours: 'Lost hours',
      company: 'Company',
      newCompanyName: 'New company name',
      trade: 'Trade',
      area: 'Area',
      workers: 'Workers',
      hoursPerWorker: 'Hours / worker',
      addRow: 'Add company',
      removeRow: 'Remove {{name}}',
      removeRowUnnamed: 'Remove row',
      workItems: 'Work performed',
      addWork: 'Add work item',
      removeWork: 'Remove work item',
      activity: 'Activity',
      status: 'Status',
      percent: '% complete',
      deliveries: 'Deliveries',
      addDelivery: 'Add delivery',
      removeDelivery: 'Remove delivery',
      item: 'Item',
      quantity: 'Quantity',
      supplier: 'Supplier',
      time: 'Time',
      safety: 'Safety',
      toolboxTalk: 'Toolbox talk topic',
      noIncidents: 'Manager stated there were no incidents',
      incidents: 'Incidents',
      addIncident: 'Add incident',
      removeIncident: 'Remove incident',
      description: 'Description',
      severity: 'Severity',
      severities: {
        near_miss: 'Near miss',
        first_aid: 'First aid',
        recordable: 'Recordable',
        lost_time: 'Lost time',
      },
      nextWorkday: 'Next workday',
      addNext: 'Add item',
      removeNext: 'Remove item',
      required: 'Required.',
      number: 'Enter a number.',
      timeInvalid: 'Use HH:MM (24 h).',
      companyRequired: 'Pick a company or type a new name.',
      noChanges: 'Nothing changed.',
      saveTitle: 'Save {{no}} as a new version?',
      saveBody: 'This becomes version {{version}}. The previous version stays in history.',
      cancelTitle: 'Discard changes?',
      cancelBody: 'Your edits will be lost.',
      discard: 'Discard',
      keepEditing: 'Keep editing',
      photos: {
        title: 'Photos',
        hint: 'Photos from your phone or computer. They are resized and uploaded when you save.',
        pdfHint_one: 'The PDF prints the first photo; move the one it should show to the front.',
        pdfHint_other:
          'The PDF prints the first {{count}}; move the ones it should show to the front.',
        inPdf: 'In PDF',
        moveEarlier: 'Move photo {{n}} earlier',
        moveLater: 'Move photo {{n}} later',
        add: 'Add photos',
        count: '{{n}} of {{max}}',
        countLabel_one: '{{count}} photo of {{max}}',
        countLabel_other: '{{count}} photos of {{max}}',
        full: 'A report holds up to {{max}} photos. Remove one to add another.',
        empty: 'No photos yet.',
        alt: 'Photo {{n}}',
        caption: 'Caption for photo {{n}}',
        captionPlaceholder: 'Caption (optional)',
        remove: 'Remove photo {{n}}',
        preparing: 'Preparing…',
        noPreview: 'Preview not available',
        taken: 'Taken {{when}}',
        dismiss: 'Dismiss',
        notImage: '{{name}} is not an image.',
        tooLarge: '{{name}} is larger than 25 MB.',
        tooManyPixels:
          '{{name}} is a {{megapixels}}-megapixel photo, too large to prepare here. Use a copy under 50 megapixels.',
        heic: '{{name}} is a HEIC photo this browser can’t open. Use Safari, or save it as JPEG first.',
        unreadable: '{{name}} couldn’t be opened. Try a JPEG or PNG.',
        limit_one: 'A report holds up to {{max}} photos; {{count}} photo was not added.',
        limit_other: 'A report holds up to {{max}} photos; {{count}} photos were not added.',
        notReady: 'A photo couldn’t be prepared. Check the Photos section and save again.',
        uploading: (i: number, n: number) => `Uploading photos ${i} of ${n}`,
        uploadingProgress: 'Uploading photos {{i}} of {{n}}',
        uploadFailed: 'A photo couldn’t be uploaded. Nothing was saved; try again.',
      },
    },
  },

  /** P8 · Crew hours (F19). EN/TR since W3-06; the Turkish is in packages/shared/src/copy/ui/tr.ts. */
  hours: {
    title: 'Crew hours',
    subtitle: 'Subcontractor hours by company, checked against invoices',
    /** The page named inside the error state ("Couldn't load crew hours"). */
    thing: 'crew hours',
    payrollCsv: 'Payroll CSV',
    weekPicker: 'Week',
    previousWeek: 'Previous week',
    nextWeek: 'Next week',
    siteSelect: 'Site',
    allSites: 'All sites',
    kpisLabel: 'Key figures',
    kpis: {
      subHours: 'Subcontractor hours',
      companies: 'Companies on site',
      gaps: 'Invoice gaps found',
      gapsDetail_one: '{{hours}} · {{count}} sub',
      gapsDetail_other: '{{hours}} · {{count}} subs',
    },
    columns: {
      company: 'Company',
      reported: 'Reported',
      invoiced: 'Invoiced',
      gap: 'Gap',
    },
    gap: { match: 'match', awaiting: 'awaiting', confirm: 'Confirm', unconfirmed: 'Unconfirmed' },
    addInvoice: 'Add invoice',
    invoiceCheck: 'Invoice check',
    invoice: '{{no}} · {{period}}',
    compare: { day: 'Day', invoice: 'Invoice', reports: 'Reports', gap: 'Gap' },
    evidence: 'Evidence',
    sendQuery: 'Send query with evidence',
    markResolved: 'Mark resolved',
    queried: 'Query sent',
    resolved: 'Marked resolved',
    empty: 'No hours reported this week.',
    selectRow: 'Select a row with a gap to compare it with the invoice.',
    selectRowTitle: 'Invoice check',
    perCompanyHint: 'Invoices are checked per company. Choose All sites to compare them.',
    unconfirmedHint: 'Heard in a report but not yet a known subcontractor.',
    /** §2.2: an accounts user cannot open reports or the glossary to confirm a company. */
    unconfirmedAsk:
      'Heard in a report but not yet a known subcontractor. Ask an ops user to confirm it.',
    openRow: 'Compare {{company}} with its invoice',
    totalRow: 'Total',
    evidenceFor: '{{date}} · {{site}}',
    /** Evidence chips (P8): "audio 0:41" and "call 2:15" are `source.links`; these have no second. */
    evidenceKinds: {
      voiceNote: 'voice note',
      answer: 'answer',
      report: 'report',
    },
    openReport: 'Open report {{no}}',
    openReportAt: 'Open report {{no}} at {{source}}',
    /** A gap day without words to quote (typed report, or no transcript to read): the crew lines. */
    crewQuote: '{{company}} · {{lines}} = {{total}}',
    crewLine: '{{workers}} × {{hours}}',
    noEvidence: 'No gap days to show evidence for.',
    noReportEvidence: 'No site report recorded hours for this company on the gap days.',
    queryMail: {
      subject: 'Invoice {{no}} — hours query from TalkOlog',
      intro:
        'Hello {{company}},\n\nInvoice {{no}} ({{period}}) shows more hours than our daily site reports. Day by day:',
      row: '{{date}}: invoiced {{invoiced}}, reported {{reported}}, gap {{gap}}',
      total: 'Total: invoiced {{invoiced}}, reported {{reported}}, gap {{gap}}',
      evidence: 'Site report evidence:',
      evidenceLine: '- {{date}} · {{site}} · {{reportNo}} · {{source}}: “{{quote}}”',
      outro:
        'Could you check these days and send a corrected invoice or the timesheets behind the difference?\n\nThanks,',
    },
    addDialog: {
      title: 'Add invoice',
      description: 'Record a subcontractor invoice for this week and compare it with the reports.',
      company: 'Company',
      companyPlaceholder: 'Choose a company',
      invoiceNo: 'Invoice number',
      invoiceNoPlaceholder: 'e.g. BL-3381',
      periodStart: 'Period start',
      periodEnd: 'Period end',
      hours: 'Hours per day',
      hoursHint: 'Prefilled from this week’s reports. Change them to match the invoice.',
      total: 'Invoice total',
      file: 'Invoice PDF',
      fileHint: 'Optional · PDF up to 10 MB',
      save: 'Add invoice',
      companyRequired: 'Choose a company.',
      invoiceNoRequired: 'Enter the invoice number.',
      periodInvalid: 'The period end must be on or after the start.',
      hoursInvalid: 'Hours must be numbers.',
      noCompanies:
        'No subcontractors yet. A company appears here once a filed report lists its crew.',
    },
  },

  weather: {
    title: 'Weather & delays',
    subtitle: 'Every lost hour, with the voice note or call, photos and weather data behind it',
    /** The page named inside the error state ("Couldn't load weather & delays"). */
    thing: 'weather & delays',
    buildPack: 'Build EOT evidence pack',
    kpisLabel: 'Key figures',
    kpis: {
      siteDays: 'Weather-affected site-days',
      hours: 'Hours of stoppage',
      claimReady: 'Claim-ready entries',
      claimReadyHint: 'audio + photo + weather data',
    },
    heatmap: 'Stoppage hours by site and day',
    heatmapCell: '{{site}}, {{date}}: {{hours}} lost',
    log: 'Delay log',
    fullDay: 'Full day',
    observed: 'Observed precip {{precip}}',
    /** Spec §19 Q134: a report filed without the site manager's confirmation. */
    notConfirmed: 'Not confirmed by the site manager: claim-ready once signed off',
    pack: {
      title: 'EOT evidence pack',
      site: 'Site',
      period: 'Period',
      sections: 'Included sections',
      sectionNames: {
        summary: 'Summary',
        reports: 'Daily reports',
        transcripts: 'Transcript excerpts',
        photos: 'Photos',
        weather: 'Weather data',
      },
      generate: 'Generate PDF pack',
      history: 'Pack history',
      requested: 'Pack requested — you’ll get a notification when it’s ready.',
      download: 'Download',
    },
    empty: 'No weather delays this month.',
    noSitesTitle: 'No sites yet',
    noSites: 'Lost hours show up here once a site’s reports record a weather delay.',
    noWorkdays: 'No workdays in this month yet.',
    monthSelect: 'Month',
    siteSelect: 'Site',
    allSites: 'All sites',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    lostLabel: 'lost',
    entries_one: '{{count}} entry',
    entries_other: '{{count}} entries',
    showingOf: 'Showing {{shown}} of {{total}}',
    openReport: 'Open report {{no}}',
    stoppedBetween: 'Stopped {{start}}–{{end}}',
    stoppedFrom: 'Stopped from {{start}}',
    packDialog: {
      description:
        'The pack has a cover, a delay summary by day, each day’s report with how it was confirmed, transcript excerpts with timestamps, photos and the observed weather table, in your company’s report language.',
      from: 'From',
      to: 'To',
      siteRequired: 'Pick the site the claim is for.',
      periodInvalid: 'The end must be on or after the start.',
      sectionsRequired: 'Pick at least one section.',
      sitePlaceholder: 'Choose a site',
      summary_one: '{{count}} site-day · {{hours}} lost',
      summary_other: '{{count}} site-days · {{hours}} lost',
      summaryMonthOnly_one: '{{month}} only: {{count}} site-day · {{hours}} lost',
      summaryMonthOnly_other: '{{month}} only: {{count}} site-days · {{hours}} lost',
    },
    packs: {
      empty: 'No packs yet. Build one from the delay log.',
      allSites: 'All sites',
      requestedBy: 'Requested {{when}}',
      period: '{{from}} – {{to}}',
      sections_one: '{{count}} section',
      sections_other: '{{count}} sections',
      demoDownload: 'Demo mode: the worker renders the PDF; nothing to download here.',
      notStarted:
        'Not started after 10 minutes. Evidence packs aren’t being built for your company right now. Contact TalkOlog support.',
    },
  },

  /**
   * `PhoneInput` (W4-01): the country-code picker (libphonenumber's regions, named by the browser
   * in the panel's language) and the number field, in Setup and Team's invite.
   */
  phone: {
    countryCode: 'Country code',
    /** The picker button's accessible name: the region's name, then its visible "US +1" (2.5.3). */
    countryButton: 'Country code: {{country}}, {{region}} +{{code}}',
    search: 'Search countries',
    searchPlaceholder: 'Country or +code',
    noMatch: 'No country matches “{{query}}”.',
    hint: 'Stored in international format, e.g. {{example}}',
    invalid: 'Enter a valid mobile number for the chosen country.',
    /** Spec §19 Q23: the org's pack applies to every reporter; a warning, never a block. */
    foreign: 'This is a {{country}} number. TalkOlog texts it from your {{pack}} number.',
  },

  /**
   * P10 Team & numbers (W4-03: EN/TR through `t()`; the v1.2 statuses, preference and message
   * channel, devices, today's link and office users' UI language).
   */
  team: {
    title: 'Team & numbers',
    subtitle:
      'TalkOlog matches every call and text to a site by the phone number. Personal links are tied to one person.',
    thing: 'team',
    inviteReporter: 'Invite reporter',
    unknown: {
      title: 'Unknown number tried to report',
      body: '{{phone}} · {{time}}',
      link: 'Link to {{site}}',
      linkOther: 'Link to a site…',
      ignore: 'Ignore',
      snippet: '“{{text}}”',
      guessed: 'Mentions {{site}}',
    },
    link: {
      title: 'Link {{phone}} to a site',
      description: 'The held message is processed as soon as the number is linked.',
      /** A caller or texter (W1-16): invited until they consent (F3, F13 step 4). */
      descriptionConsent:
        'TalkOlog texts them a welcome with their link. They can report once they agree to texts and recording.',
      name: 'Name',
      namePlaceholder: 'Who is this?',
      role: 'Role',
      site: 'Site',
      sitePlaceholder: 'Choose a site',
      confirm: 'Link number',
      nameRequired: 'Enter a name.',
      siteRequired: 'Choose a site.',
    },
    reportsCount_one: '{{count}} report in 30 days',
    reportsCount_other: '{{count}} reports in 30 days',
    /** P10 "Prefers": the report mode (§2.3), with the message channel when one is set (Q21). */
    prefers: {
      link: 'Text link',
      call: 'Call',
      withChannel: '{{pref}} · {{channel}}',
    },
    /** §4.5 message channels, as Team and Edit name them. */
    channels: {
      sms: 'SMS',
      whatsapp: 'WhatsApp',
      push: 'Notifications',
    },
    /** §19 Q243: a channel saved without its consent stays unreachable until they consent. */
    channelNotAgreed: '{{channel}} not agreed yet',
    /** The language TalkOlog uses with the reporter (`reporters.locale`). */
    locales: {
      'en-US': 'English',
      'es-US': 'Spanish',
      'tr-TR': 'Turkish',
    },
    speaks: 'Speaks {{languages}}',
    primary: 'Primary',
    noSites: 'No sites',
    empty: 'No reporters yet. Invite the first site manager.',
    /** P10 statuses: never by colour alone (each chip has its word). */
    status: {
      active: 'Active',
      invite_sent: 'Invite sent',
      opted_out: 'Opted out of texts',
      undeliverable: 'Can’t text this number',
      disabled: 'Disabled',
    },
    /** Under a status: who stopped texts (§19 Q242), the automatic re-send (Q241), devices. */
    statusNotes: {
      byPerson: 'Stopped by them (STOP)',
      byProvider: 'Stopped by the phone company',
      autoResent: 'Re-sent automatically',
      devices_one: '{{count}} phone signed in',
      devices_other: '{{count}} phones signed in',
    },
    edit: {
      title: 'Edit {{name}}',
      description: 'Changes apply to the next text or call.',
      phone: 'Mobile number',
      phoneHint:
        'The number can’t be changed. To use a new number, disable this reporter and invite the new number.',
      preference: 'Prefers',
      messageChannel: 'Messages go by',
      channelDefault: 'Your country’s order ({{order}})',
      language: 'Language TalkOlog uses',
      languageHint: 'Texts, the recorder and calls are in this language.',
      /** §19 Q243: required to change the preference or the channel. */
      attest: '{{first}} asked for this change',
      attestHint: 'Saved in the log with your name.',
      attestRequired: 'Tick the box: change how TalkOlog reaches {{first}} only when they asked.',
      channelNotAgreed:
        '{{first}} has not agreed to {{channel}} yet. Messages go another way until they turn it on in the recorder.',
      save: 'Save changes',
    },
    officeEmpty: 'No office users.',
    changeRole: 'Change role',
    removeUser: 'Remove',
    you: 'you',
    disableDialog: {
      title: 'Disable {{name}}?',
      body: 'TalkOlog stops texting and calling them, and their link stops working. Re-send the welcome to enable them again.',
    },
    removeDialog: {
      title: 'Remove {{name}}?',
      body: 'They lose access to this company right away. You can invite them again later.',
      confirm: 'Remove user',
    },
    /** F16 lost phone (§19 Q244). */
    revokeDialog: {
      title: 'Revoke devices for {{name}}?',
      body: 'Every phone that opened {{first}}’s link is signed out. Their next open asks for a code sent to {{phone}}. Their consents and personal link stay.',
      count_one: 'Signed in now: {{count}} phone.',
      count_other: 'Signed in now: {{count}} phones.',
      confirm: 'Revoke devices',
    },
    /** §19 Q242: one-way packs only, an opt-out the phone company recorded. */
    optOutDialog: {
      title: 'Turn texts back on for {{name}}?',
      body: 'The phone company stopped texts to this number. Do this only if {{first}} asked to get texts again. The phone company may still refuse them.',
      attest: '{{first}} asked to get texts again',
      confirm: 'Turn texts back on',
    },
    /**
     * W4-04 (F3, §19 Q230): Call now. The attestation sentence is Q230's, stored with its
     * version (`team.call_now.v1`) as an `auto_call` consent `office_attested`.
     */
    callNow: {
      title: 'Call {{name}} now?',
      body: 'TalkOlog’s assistant calls {{first}} now about today’s report at {{site}}. {{first}} first hears that the call is recorded.',
      attest: '{{first}} agreed to get calls from TalkOlog for the daily report',
      attestHint:
        'Saved in the log with your name. It allows this call only: reminder calls still need {{first}}’s own agreement in the recorder.',
      noText: 'If {{first}} doesn’t answer, TalkOlog sends no text. You see how it went here.',
      confirm: 'Call now',
      /** office_call_start()'s refusals (`OFFICE_CALL_REFUSALS`). */
      refused: {
        attestation_required: 'Tick the box first: the person must have agreed to get calls.',
        no_trunk: 'TalkOlog can’t place calls for your company yet.',
        reporter_inactive:
          'This reporter is disabled or opted out of texts. TalkOlog doesn’t call them.',
        calls_turned_off:
          'They turned automated calls off in the recorder. Only they can turn them back on.',
        no_calls_today: 'They asked not to be called today.',
        site_not_active: 'The site is paused or archived.',
        outside_window: 'It is outside the hours TalkOlog calls at this site.',
        day_filed: 'Today’s report for this site is already filed.',
        call_live: 'A call to them is already under way.',
        limit_reached: 'The office has called them as often as allowed today.',
      },
    },
    /** W4-04: the latest outbound call today, under the status (never by colour alone). */
    callToday: {
      office: 'Office call',
      reminder: 'Reminder call',
      line: '{{kind}} · {{progress}} · {{time}}',
    },
    /** A call's progress from `calls.status` (`callProgress`). */
    callProgress: {
      requested: 'Requested',
      ringing: 'Ringing',
      answered: 'Answered',
      not_reached: 'Not reached',
      failed: 'Couldn’t be placed',
    },
    /** Why Team offers no way back on a two-way pack (START by the person only). */
    optOutStartOnly:
      'Only {{first}} can turn texts back on, by replying START to one of our texts.',
    reporters: 'Field reporters',
    columns: {
      name: 'Name',
      mobile: 'Mobile',
      prefers: 'Prefers',
      sites: 'Sites',
      language: 'Language',
      onTime: 'On time · 30 d',
      status: 'Status',
    },
    roles: { site_manager: 'Site manager', backup: 'Backup' },
    rowMenu: {
      edit: 'Edit',
      resendWelcome: 'Re-send welcome',
      textLink: 'Text today’s link',
      textLinkAt: 'Text today’s link · {{site}}',
      revokeDevices: 'Revoke devices',
      clearOptOut: 'Turn texts back on',
      callNow: 'Call now',
      callNowAt: 'Call now · {{site}}',
      enable: 'Enable',
      disable: 'Disable',
    },
    office: 'Office users',
    addOfficeUser: 'Add office user',
    officeRoles: { admin: 'Admin', pm: 'Project manager', ops: 'Operations', accounts: 'Accounts' },
    /** F24: an office user's panel language (`org_members.ui_locale`). */
    uiLocales: {
      'en-US': 'English',
      'tr-TR': 'Turkish',
    },
    officeLanguage: 'Panel in {{language}}',
    howMatching: 'How matching works',
    howMatchingBody:
      'Each reporter’s phone number and personal link are tied to their sites. A call or text from a linked number goes to those sites; an unknown number never creates a report. It is held here until you link or ignore it.',
    invite: {
      title: 'Invite reporter',
      name: 'Name',
      phone: 'Mobile number',
      role: 'Role',
      sites: 'Sites',
      languages: 'Speaks',
      language: 'Language TalkOlog uses',
      preference: 'Preferred channel',
      preferenceHint: 'They can always use the other one.',
      /** §19 Q33: a call preference where the pack has no voice number. */
      callTalkNow: 'They will use Talk now in the recorder.',
      send: 'Send welcome text',
      /**
       * Owner decision 2026-10-20 (Twilio toll-free verification, opt-in "Verbal"): required to add
       * a person, kept with its version (`team.invite_sms.v2`) in the `reporter.invited` log entry.
       * Also the setup wizard's, for the site manager and the backup.
       */
      smsAttest: '{{first}} agreed to get daily report messages from TalkOlog by text or WhatsApp',
      /** The same before a name is typed. */
      smsAttestNoName:
        'This person agreed to get daily report messages from TalkOlog by text or WhatsApp',
      smsAttestHint: 'Saved in the log with your name.',
      attestation_required:
        'Tick the box first: the person must have agreed to get daily report messages.',
      description:
        'Texts the reporter their personal link. They open it on their phone, confirm the number with a code and agree to recording.',
      namePlaceholder: 'Full name',
      nameRequired: 'Enter a name.',
      sitesRequired: 'Pick at least one site.',
      noSites: 'Add a site first. Each reporter reports for a site.',
      noSitesAskAdmin: 'Ask an admin to add a site first. Each reporter reports for a site.',
      addSite: 'Add a site',
      /** The org's report language (§9.6, `orgs.locale`), named as the chips name it. */
      languagesHint: 'Reports always come out in {{language}}.',
      languageNames: {
        en: 'English',
        es: 'Spanish',
        pl: 'Polish',
        pt: 'Portuguese',
        hi: 'Hindi',
        fr: 'French',
        tr: 'Turkish',
      },
    },
    addUser: {
      title: 'Add office user',
      description: 'They get a magic-link email and join with the role you choose.',
      email: 'Email',
      emailPlaceholder: 'name@company.com',
      emailInvalid: 'Enter a valid email.',
      role: 'Role',
      uiLanguage: 'Panel language',
      uiLanguageOrg: 'The company’s language ({{language}})',
      send: 'Send invite',
      lastAdmin: 'You can’t remove the last admin.',
      roleHelp: {
        admin: 'Everything, including settings and billing',
        pm: 'Sites, reports, hours, weather, glossary',
        ops: 'Everything except settings and billing',
        accounts: 'Crew hours and payroll only',
      },
    },
    toasts: {
      invited: 'Welcome text queued for {{name}}.',
      /** api-invite-reporter answered `opted_out`: the number sent STOP, so nothing is texted. */
      invitedOptedOut: '{{name}} opted out of texts (STOP); no welcome text was sent.',
      linked: 'Number linked.',
      ignored: 'Number ignored.',
      resent: 'Welcome text re-sent to {{name}}.',
      disabled: '{{name}} disabled.',
      enabled: '{{name}} enabled. A new welcome text is on its way.',
      updated: '{{name}} updated.',
      devicesRevoked: '{{name}} is signed out on every phone. The next open asks for a code.',
      optOutCleared: 'Texts turned back on for {{name}}.',
      callRequested: 'Calling {{name}} now. The progress shows in their row.',
      memberAdded: 'Invite sent to {{email}}.',
      memberUpdated: 'Role updated.',
      memberRemoved: 'User removed.',
    },
    /** The RPCs' refusals (CodedError codes), in the panel's language. */
    errors: {
      attestation_required: 'Tick the box first: the person must have asked for this.',
      channel_not_offered: 'That channel is not offered in your country.',
      locale_not_offered: 'That language is not offered in your country.',
      two_way_pack: 'Only the person can turn texts back on, by replying START.',
      opted_out_by_person: 'They stopped texts themselves. Only they can turn them back on.',
      not_opted_out: 'Texts are not stopped for this number.',
    },
  },

  /** P11 Site glossary (W3-08: EN/TR). */
  glossary: {
    title: 'Site glossary',
    subtitle: 'What the field says, written the way reports should read',
    thing: 'site glossary',
    addTerm: 'Add term',
    reviewTitle: 'New this week · review {{n}}',
    heard: 'Heard {{times}}×',
    heardAt: 'Heard {{times}}× · {{site}}',
    approve: 'Approve',
    change: 'Change',
    reject: 'Reject',
    addAsSub: 'Add as subcontractor',
    mapTo: 'Map to…',
    all: 'All terms',
    tabs: { all: 'All', companies: 'Companies', spanish: 'Spanish', turkish: 'Turkish' },
    columns: {
      heard: 'Heard in the field',
      official: 'Written in reports as',
      scope: 'Scope',
      times: 'Times',
      source: 'Source',
      actions: 'Actions',
    },
    scope: { org: 'All sites' },
    search: 'Search terms',
    empty: 'No terms yet. The agent learns them from voice notes.',
    emptyReview: 'Nothing to review.',
    emptyReviewBody: 'New phrases from voice notes land here once a week’s reports are in.',
    form: {
      title: 'Add term',
      description: 'Teach the agent a phrase the field uses and how it should read in reports.',
      heard: 'Heard in the field',
      heardPlaceholder: 'e.g. the deck',
      official: 'Written in reports as',
      officialPlaceholder: 'e.g. Elevated concrete slab',
      kind: 'Kind',
      language: 'Language',
      scope: 'Scope',
      allSites: 'All sites',
      save: 'Add term',
      heardRequired: 'Enter the phrase as heard.',
      officialRequired: 'Enter the official term.',
    },
    kinds: { term: 'Term', company: 'Company', area: 'Area', unit: 'Unit' },
    languages: {
      en: 'English',
      es: 'Spanish',
      tr: 'Turkish',
      pl: 'Polish',
      hi: 'Hindi',
      pt: 'Portuguese',
    },
    arrow: 'written as',
    unknownCompany: 'Unknown company',
    changeLabel: 'Official term',
    saveChange: 'Save',
    mapToLabel: 'Map to existing company',
    mapToPlaceholder: 'Choose a company',
    mapConfirm: 'Map',
    noResults: 'No terms match “{{q}}”.',
    /** W4-08 (§19 Q238): TalkOlog's starter terms. */
    builtIn: {
      changeTitle: 'Change built-in term',
      changeDescription:
        'How should reports write “{{heard}}”? Saving makes it your company’s confirmed term.',
      changeTerm: 'Change “{{heard}}”',
      rejectTerm: 'Reject “{{heard}}”',
    },
  },

  /**
   * P12 · Settings (W4-05, EN/TR): Reports & reminders on v1.2, Field channels (the country pack
   * read-only, the daily link, calls, new reporters, the recording notice, the test message), PDF
   * template with the report language and units (§19 Q233), Recipients, Payroll export.
   */
  settings: {
    title: 'Settings',
    /** The error state's "Couldn’t load {{thing}}." */
    thing: 'settings',
    tabs: {
      reports: 'Reports & reminders',
      pdf: 'PDF template',
      recipients: 'Recipients',
      payroll: 'Payroll export',
      /** v1.2 `/settings/channels` (§11.1, P12; W4-05). */
      fieldChannels: 'Field channels',
    },
    save: 'Save changes',
    saved: 'Settings saved.',
    deadline: 'Report deadline',
    workingDays: 'Working days',
    ladder: 'Reminder ladder',
    stages: {
      dailyLink: 'Daily link',
      headsUp: 'Weather heads-up',
      reminder1: 'Reminder 1',
      reminder2: 'Reminder 2',
      missing: 'Missing · office alerted',
    },
    offset: 'Offset (minutes)',
    offsetHint: 'Minutes relative to the {{deadline}} deadline',
    offsetMinutes: '{{offset}} min',
    stageTime: 'at {{time}}',
    stageHints: {
      dailyLink: 'Every reporter gets today’s link by text.',
      headsUp: 'Only on days with rain or wind in the forecast',
      reminder1: 'Text or call — per person',
      reminder2: 'A second text; a second call for people who prefer calls, when it is on',
      missing: 'Office is notified and the site-day is marked missing',
    },
    /** The daily link card's link to the tab where its time is set. */
    dailyLinkChange: 'Change on Field channels',
    required: 'Every report must include',
    requiredHint:
      'Missing items trigger one question on the recorder or the call before the draft.',
    requiredFields: {
      manpower: 'Crew by company',
      hours: 'Hours',
      weather: 'Weather',
      work_performed: 'Work performed',
      safety: 'Safety',
      photo: 'At least one photo',
    },
    filing: 'Filing',
    filingConfirm: 'Reporter confirms — on the recorder or by voice on the call',
    filingConfirmHint:
      'The draft is filed after the reporter confirms. Unanswered drafts file after 2 hours.',
    filingAuto: 'File automatically after 15 minutes',
    filingAutoHint: 'Fastest paperwork; corrections create a new version.',
    signoffRequired: 'Sign-off required',
    signoffRequiredHint: 'An office user signs each report off in the panel.',
    emailPdf: 'Email PDF when filed',
    emailPdfHint: 'Recipients get the PDF the moment a report is filed.',
    deadlineHint: 'Default for new sites · each site can override it',
    /** ISO weekdays 1–7 (`org_settings.working_days`). */
    weekdays: {
      d1: 'Mon',
      d2: 'Tue',
      d3: 'Wed',
      d4: 'Thu',
      d5: 'Fri',
      d6: 'Sat',
      d7: 'Sun',
    },
    workingDaysHint: 'Reminders and “missing” only count working days.',
    /** Field channels (P12, F22; W4-05). */
    channels: {
      pack: 'Your country pack',
      packHint: 'Set by TalkOlog per country. Ask TalkOlog to change it.',
      country: 'Country',
      sender: 'Text sender',
      senderTypes: {
        toll_free: 'Toll-free number',
        long_code: 'Long code',
        short_code: 'Short code',
        alphanumeric: 'Sender name',
      },
      senderNotSet: 'Not set up in this environment',
      voiceNumber: 'Number to call TalkOlog',
      talkNowOnly: 'Talk now only',
      channelOrder: 'Message order',
      twoWay: 'Replies to texts',
      twoWayYes: 'Two-way: STOP, HELP and replies reach TalkOlog',
      twoWayNo: 'One-way: texts cannot be answered',
      links: 'Links in texts',
      linksYes: 'Allowed',
      linksNo: 'Not allowed',
      packMissing: 'Your country pack could not be loaded.',
      dailyLink: 'Daily link',
      dailyLinkTime: 'Daily link time',
      dailyLinkTimeHint: 'Every reporter gets today’s link by text at this time, site time.',
      ttl: 'Daily link expiry (hours)',
      ttlHint: 'Empty: TalkOlog’s default. Whole hours, 1–168.',
      ttlInvalid: 'Enter whole hours from 1 to 168, or leave it empty.',
      calls: 'Calls',
      autoCall: 'Call people who prefer calls',
      autoCallHint: 'At reminder 1, inside the call window, when they agreed to calls.',
      secondCall: 'Second call at reminder 2',
      secondCallHint: 'A second call, with the reminder text.',
      callWindow: 'Call window',
      callWindowHint: 'Site time. Calls and reminder texts go out only inside it.',
      callWindowFrom: 'From',
      callWindowTo: 'To',
      noCalls:
        'TalkOlog does not place calls in {{country}} yet. People who prefer calls use Talk now in the recorder; reminder texts keep to the call window.',
      newReporters: 'New reporters',
      defaultPref: 'Default preference',
      defaultPrefHint: 'Setup and Invite start with it. They can always use the other one.',
      notice: 'Recording notice',
      noticeHint: 'Said at the start of every call. TalkOlog sets and versions it per country.',
      noticeVersion: 'Version {{version}}',
      test: 'Test',
      testHint: 'See what your site managers receive.',
      testMessage: 'Send me a test message',
      testDialog: {
        title: 'Send me a test message',
        body: 'TalkOlog texts a test message to this number from your country’s sender. The number is not saved.',
        phone: 'Your mobile number',
        language: 'Language of the text',
        attest: 'This is my own number.',
        attestRequired: 'Tick the box to confirm it is your own number.',
        send: 'Send test message',
      },
      testResults: {
        sent: 'Test message sent. It should arrive within a minute.',
        demo: 'Demo company: nothing was sent. Real companies get it from the country’s sender.',
        noSender:
          'No text sender is set up for {{country}} in this environment, so nothing was sent.',
        smsOff:
          'Texts are off for {{country}}: TalkOlog reaches site managers by WhatsApp and app notifications, so nothing was sent.',
        failed: 'The text provider refused the test message. TalkOlog has logged it.',
        rateLimited: 'Five test messages an hour. Try again later.',
        invalidPhone: 'Enter a valid mobile number.',
      },
      callMeNow: 'Call me now',
      callDialog: {
        title: 'Call me now',
        body: 'TalkOlog calls this number with a short test call: who is calling, one sentence and goodbye. Nothing is recorded and the number is not saved.',
        phone: 'Your mobile number',
        language: 'Language of the call',
        attest: 'This is my own number.',
        attestRequired: 'Tick the box to confirm it is your own number.',
        call: 'Call me now',
      },
      callResults: {
        requested: 'Calling you now. Your phone should ring within a minute.',
        demo: 'Demo company: the call was placed on TalkOlog’s console, so no real phone rings.',
        rateLimited: 'Five test calls an hour. Try again later.',
        callLive: 'A test call is still on its way. Wait for it to end.',
        noTrunk: 'TalkOlog does not place calls in {{country}} yet.',
        invalidPhone: 'Enter a valid mobile number.',
      },
    },
    pdf: {
      logo: 'Logo',
      logoHint: 'PNG or SVG up to 2 MB · shown on every PDF',
      upload: 'Upload logo',
      replace: 'Replace logo',
      removeLogo: 'Remove logo',
      logoDemo: 'Demo mode: the logo is kept in this browser tab only.',
      logoUploading: 'Uploading…',
      logoWrongType: 'Use a PNG or SVG file.',
      logoTooLarge: 'The logo must be 2 MB or smaller.',
      letterhead: 'Letterhead lines',
      letterheadHint: 'Address, licence number — one per line, under the company name.',
      letterheadPlaceholder: 'Line',
      letterheadLine: 'Line {{n}}',
      addLine: 'Add line',
      removeLine: 'Remove line',
      removeLineN: 'Remove line {{n}}',
      footer: 'Footer',
      footerText: 'Generated by TalkOlog from the site manager’s voice report',
      footerToggle: 'Show “Generated by TalkOlog…” footer',
      paper: 'Paper size',
      letter: 'Letter',
      a4: 'A4',
      photos: 'Photos per report',
      photosHint: 'Up to 4 on the daily PDF.',
      preview: 'Preview · page 1',
      previewReport: 'Daily report',
      previewNo: 'RL-2041-0187 · v2',
      previewLogo: 'LOGO',
      /** §19 Q233 (W4-05): `update_org` p_locale / p_units, admin, audited. */
      reportLocale: 'Report language and units',
      reportLocaleHint: 'New report versions use them. Filed versions keep their own.',
      reportLanguage: 'Report language',
      reportLanguages: { en: 'English', tr: 'Turkish' },
      units: 'Units',
      unitNames: {
        imperial: 'Imperial (°F, in, mph)',
        metric: 'Metric (°C, mm, km/h)',
      },
      reportLocaleApply: 'Change language and units',
      reportLocaleConfirmTitle: 'Change the report language and units?',
      reportLocaleConfirmBody:
        'New report versions will be in {{language}}, {{units}}. Filed versions keep their language and units.',
      reportLocaleConfirm: 'Change',
      reportLocaleSaved: 'Report language and units saved.',
    },
    recipients: {
      perSite: 'Per-site recipients',
      hint: 'Filed PDFs and the daily digest go to these addresses.',
      applyAll: 'Apply to all sites',
      applyAllHint: (site: string) => `Copies ${site}’s list to every other site.`,
      /** W4-05: the same, as i18next strings (Settings; the setup wizard keeps the functions). */
      applyAllFor: 'Copies {{site}}’s list to every other site.',
      applyAllSite: 'Apply to all sites · {{site}}',
      applied: 'Recipients applied to all sites.',
      add: 'Add email',
      addSite: 'Add email · {{site}}',
      placeholder: 'name@company.com',
      remove: (email: string) => `Remove ${email}`,
      removeEmail: 'Remove {{email}}',
      perSiteOf: 'Per-site recipients · {{site}}',
      invalid: 'Enter a valid email.',
      none: 'No recipients yet.',
      noSitesTitle: 'No sites yet',
      noSitesBody: 'Recipients are set per site. Add a site first.',
    },
    payroll: {
      format: 'Format',
      formats: { generic: 'Generic CSV' },
      formatHint: 'More presets (QuickBooks, ADP) are coming.',
      columns: 'Columns',
      columnsHint: 'Column order of the payroll CSV, for everyone in the company.',
      reset: 'Reset to default order',
      moveUpCol: 'Move {{column}} up',
      moveDownCol: 'Move {{column}} down',
      includeUnconfirmed: 'Include unconfirmed companies',
      includeUnconfirmedHint: 'Rows heard in reports but not yet confirmed as subcontractors.',
    },
    unsavedTitle: 'Unsaved changes',
    unsavedBody: 'You have unsaved settings. Leave without saving?',
    leave: 'Leave',
    stay: 'Stay',
    discard: 'Discard changes',
  },

  /**
   * P13 · Billing (W4-10, EN/TR): the org's own price (spec §19 Q234) in its currency and the
   * panel's language, the provider (§19 Q236: `manual` → TalkOlog invoices directly), the admin
   * banner on every page (F23, §19 Q235).
   */
  billing: {
    title: 'Billing',
    subtitle: 'Billed per active site, per month. No setup fee.',
    subtitlePrice: '{{price}} per active site, per month. No setup fee.',
    plan: 'Current plan',
    perMonth: '/ month',
    /** "6 × $399.00 = $2,394.00": active sites × the org's price = the monthly total. */
    equation: '{{count}} × {{price}} = {{total}}',
    noPrice: 'TalkOlog sets your price per site. Ask us if it is not shown here yet.',
    nextInvoice: 'Next invoice',
    paymentMethod: 'Payment method',
    updatePayment: 'Update payment method',
    whatCounts: 'What counts as active',
    whatCountsActive: 'Active while it can receive reports.',
    whatCountsPaused: 'Paused sites stop reminders and billing from the next cycle.',
    whatCountsProrated: 'Added sites are prorated by Stripe.',
    whatCountsManual: 'Added sites are on your next invoice from TalkOlog.',
    sitesOnPlan: 'Sites on this plan',
    thing: 'billing',
    columns: {
      site: 'Site',
      since: 'Active since',
      status: 'Status',
      monthly: 'Monthly',
      action: 'Action',
    },
    pause: 'Pause',
    resume: 'Resume',
    pauseSiteLabel: 'Pause {{site}}',
    resumeSiteLabel: 'Resume {{site}}',
    pauseDialog: {
      title: 'Pause {{site}}?',
      body: 'Reminders stop and it won’t be billed from next cycle.',
      confirm: 'Pause site',
    },
    resumeDialog: {
      title: 'Resume {{site}}?',
      body: 'Reminders start again and the site is billed from today.',
      confirm: 'Resume site',
    },
    activeSitesLabel: 'Active sites',
    card: '{{brand}} ending {{last4}}',
    noPaymentMethod: 'No payment method on file',
    trial: 'Free trial',
    trialEnds: 'Trial ends {{date}}',
    trialEnded: 'Trial ended {{date}}',
    noSubscription: 'No subscription yet',
    status: 'Billing status',
    statuses: {
      trialing: 'Free trial',
      active: 'Active',
      past_due: 'Past due',
      canceled: 'Canceled',
      paused: 'Paused',
    },
    portalDemo: 'Demo mode: the Stripe customer portal opens here in production.',
    portalOpening: 'Opening the Stripe customer portal…',
    checkoutDemo: 'Demo mode: Stripe Checkout opens here in production.',
    checkoutOpening: 'Opening Stripe Checkout…',
    startTitle: 'Start your subscription',
    startBody:
      '14-day free trial. Add a card before day 15 to keep reports and reminders running; billing is per active site.',
    startCta: 'Add payment details',
    startNoSites: 'Add a site before you add payment details. Billing is per active site.',
    startResumeSite:
      'Resume a site below, or add one, before you add payment details. Billing is per active site.',
    addSite: 'Add a site',
    noSitesTitle: 'No sites on the plan',
    noSitesBody: 'Each active site is billed monthly.',
    noSitesBodyPrice: 'Each active site is billed {{price}} a month.',
    checkoutSuccess: 'Subscription started. It can take a minute to show here.',
    checkoutCanceled: 'Checkout canceled. No subscription was started.',
    pausedSince: 'since {{date}}',
    invoicesEmpty: 'No invoices yet.',
    invoiceDownload: 'Download invoice {{date}}',
    notBilled: '—',
    invoices: 'Invoices',
    invoiceColumns: { date: 'Date', sites: 'Sites', amount: 'Amount', status: 'Status' },
    download: 'Download',
    /** §19 Q236: a `manual` org (a TR org until §19 Q8). */
    manual: {
      title: 'TalkOlog invoices you directly',
      body: 'No card is needed here. TalkOlog sends your invoice for the active sites below each month; a paused site is not billed from the next cycle.',
      invoices: 'Your invoices come from TalkOlog by email.',
    },
    /** The banner on every page for admins (F23, spec §19 Q235). */
    banner: {
      label: 'Billing',
      past_due:
        'Payment past due. Update the payment method by {{date}} to keep reminders and calls on.',
      pastDueNoDate: 'Payment past due. Update the payment method to keep reminders and calls on.',
      trial_ended:
        'Your free trial ended on {{ended}}. Add payment details by {{date}} to keep reminders and calls on.',
      manualPastDue:
        'Your TalkOlog invoice is past due. Reminders and calls stop on {{date}} unless it is paid.',
      blocked:
        'Reminders and calls are paused: billing is past due. Update the payment method to restart them.',
      manualBlocked:
        'Reminders and calls are paused: your TalkOlog invoice is past due. Contact TalkOlog to restart them.',
      canceled:
        'Your subscription is canceled. Reminders and calls are off until it is restarted from Billing.',
      action: 'Open Billing',
    },
  },

  /**
   * P2 Setup wizard (F2; W4-02): read through `t()`, Turkish in `UI_RESOURCES.tr.panel.setup`.
   * The site step reuses Team's invite fields (`team.invite.language`, `preference`, `team.locales`,
   * `team.prefers`); the welcome preview is the §9.2 SMS itself (`renderSms`), not copy here.
   */
  setup: {
    title: 'Set up your company',
    header: '{{company}} · setup',
    saveLater: 'Save and finish later',
    steps: { company: 'Company', site: 'First site & manager', reports: 'Reports & reminders' },
    stepOf: 'Step {{n}} of {{total}}',
    stepState: { done: 'Done', current: 'Current step', upcoming: 'Upcoming' },
    addSiteTitle: 'Add a site',
    addSiteSubtitle: 'Each site reports daily by text link or call. No setup fee.',
    addSiteSubtitlePrice:
      'Each site reports daily by text link or call. {{price}} / month, no setup fee.',
    company: {
      title: 'Your company',
      subtitle: 'What goes on the PDFs and the welcome text.',
      name: 'Company name',
      namePlaceholder: 'e.g. Kestrel Build Group',
      nameRequired: 'Enter your company name.',
      address: 'Address',
      addressPlaceholder: 'Street, city, state',
      licenseNo: 'Licence number',
      logo: 'Logo',
      logoHint: 'PNG or SVG up to 2 MB · used on PDFs',
      logoTooLarge: 'The logo must be 2 MB or smaller.',
      logoWrongType: 'Use a PNG or SVG file.',
      upload: 'Upload logo',
      replace: 'Replace logo',
      removeLogo: 'Remove logo',
      logoUploadFailed:
        'The company was created, but the logo didn’t upload. Add it in Settings → PDF template.',
      logoSaveFailed: 'The logo didn’t upload. Add it in Settings → PDF template.',
      timezone: 'Company time zone',
      next: 'Continue to first site',
      /** Country packs (spec §4.6): one option per `countries` row, named by the browser. */
      country: 'Country',
      countryPlaceholder: 'Choose a country',
      countryHint: 'Sets the report language, units, paper size, currency and time zone.',
      countryLocked: 'The country can’t be changed after setup.',
      countryRequired: 'Choose your company’s country.',
      countryList: 'the country list',
      noCountries: 'No country is set up in this TalkOlog database yet.',
      presets: {
        title: 'Set by {{country}}',
        reportLanguage: 'Report language',
        units: 'Units',
        paper: 'Paper',
        currency: 'Currency',
      },
      unitNames: { imperial: 'Imperial', metric: 'Metric' },
      paperNames: { letter: 'Letter', a4: 'A4' },
      errors: {
        unknownCountry: 'That country isn’t available. Choose one from the list.',
      },
    },
    site: {
      sectionSite: 'Site',
      sectionManager: 'Site manager',
      name: 'Site name',
      namePlaceholder: 'e.g. Riverside Lofts — Block B',
      nameRequired: 'Enter the site name.',
      projectCode: 'Project number',
      projectCodePlaceholder: 'e.g. RL-2041',
      projectCodeRequired: 'Enter the project number.',
      projectCodeTaken: 'That project number is already in use.',
      address: 'Site address',
      addressPlaceholder: 'Street, city, state',
      addressHint: 'Used to pull local weather data for every report and delay entry.',
      addressRequired: 'Enter the address or set the coordinates.',
      geocoded: 'Located · {{lat}}, {{lng}}',
      geocoding: 'Looking up the address…',
      notGeocoded:
        'Couldn’t locate that address. Enter the coordinates or weather data stays unavailable until the location is set.',
      manualCoords: 'Set coordinates manually',
      lat: 'Latitude',
      lng: 'Longitude',
      coordsInvalid: 'Latitude is −90…90 and longitude −180…180.',
      timezone: 'Site time zone',
      deadline: 'Report deadline',
      deadlineHint: 'Local time · reminders start 15 minutes later',
      managerName: 'Name',
      managerNamePlaceholder: 'Full name',
      managerNameRequired: 'Enter the site manager’s name.',
      phone: 'Mobile number',
      languages: 'Speaks',
      backup: 'Backup reporter',
      backupHint: 'Optional · gets the same welcome and can cover for the manager.',
      addBackup: 'Add a backup reporter',
      removeBackup: 'Remove backup',
      backupName: 'Backup name',
      backupPhone: 'Backup mobile number',
      /** The backup's own language and preference (W4-02), as the manager's. */
      backupLanguage: 'Language TalkOlog uses with the backup',
      backupPreference: 'Backup’s preferred channel',
      addAnother: 'Add another site',
      send: 'Send welcome text',
      sent: 'Site added. The welcome text to {{name}} is queued.',
      inviteFailed:
        'The site was created, but the welcome text to {{name}} couldn’t be queued ({{reason}}). Invite them from Team & numbers.',
      recipientsFailed:
        'The site was created, but its report recipients weren’t saved. Set them in Settings → Recipients.',
      another_one: '{{count}} site added so far',
      another_other: '{{count}} sites added so far',
      /** F2 / §19 Q229 (W4-02): a number libphonenumber knows to be a landline. */
      lineCheck: {
        landline_text:
          'This is a landline number, which can’t get texts. Choose Call, or enter a mobile number.',
        landline_unreachable:
          'This is a landline number. TalkOlog can’t text it, and Talk now needs the personal link and a code, both sent by text. Enter a mobile number.',
      },
      preview: {
        title: 'Preview · what {{name}} receives',
        from: 'TalkOlog · text message',
        /** §19 Q25: the welcome always goes by SMS; the link in the preview is not theirs. */
        sampleLink: 'The link is a sample. Each person gets their own.',
        callAnyTime: 'They can also call {{number}} any time.',
        unavailable: 'No preview: texts in this country can’t carry a link.',
        placeholderName: 'your site manager',
        placeholderSite: 'the site',
      },
      pricing: {
        perSiteMonth: '/ site / month',
        setupBilled: '{{zero}} setup · billed per active site',
        pointPause: 'Cancel or pause any site at any time',
        pointInvoice: 'First invoice at the start of next month',
        sitesTimes_one: '{{count}} active site × {{price}}',
        sitesTimes_other: '{{count}} active sites × {{price}}',
      },
    },
    reports: {
      title: 'Reports & reminders',
      subtitle: 'Defaults for every site. Change them later in Settings.',
      recipients: 'Report recipients',
      recipientsHint: 'Filed PDFs go to these addresses. You can set per-site lists later.',
      /** The recipients' chips and add box (as Settings → Recipients words them). */
      addEmail: 'Add email',
      emailPlaceholder: 'name@company.com',
      removeEmail: 'Remove {{email}}',
      emailInvalid: 'Enter a valid email.',
      noRecipients: 'No recipients yet.',
      finish: 'Finish',
      finished: 'All set. Reports arrive after the deadline.',
    },
    resume: 'Progress saved. Pick up where you left off.',
    savedLater: 'Progress saved.',
    savedLaterSignedOut: 'Progress saved. Sign in again to finish setting up.',
    pageName: 'company setup',
  },

  ops: {
    eyebrow: 'TalkOlog internal',
    title: 'Ops',
    subtitle: 'Job queue, dead letters and report cost across every org. TalkOlog staff only.',
    errorThing: 'ops data',
    refresh: 'Refresh',
    utc: (when: string) => `${when} UTC`,
    age: (seconds: number) => {
      const s = Math.max(0, Math.round(seconds));
      if (s < 60) return `${s} s`;
      if (s < 3600) return `${Math.floor(s / 60)} min`;
      return `${Math.floor(s / 3600)} h ${Math.floor((s % 3600) / 60)} min`;
    },
    queue: {
      title: 'Queue health',
      label: 'Queue by job type',
      queued: 'Queued',
      running: 'Running',
      dead: 'Dead',
      oldest: 'Oldest waiting',
      oldestHint: 'Longest a due job has waited for a worker',
      none: 'None waiting',
      columns: {
        type: 'Job type',
        queued: 'Queued',
        running: 'Running',
        dead: 'Dead',
        oldest: 'Oldest waiting',
      },
      empty: 'The queue is empty.',
    },
    dead: {
      title: 'Dead jobs',
      hint: 'Jobs that failed 5 attempts. Fix the cause, then retry; discard what should never run.',
      columns: {
        job: 'Job',
        attempts: 'Attempts',
        error: 'Last error',
        payload: 'Payload',
        died: 'Died',
      },
      retry: 'Retry',
      discard: 'Discard',
      retryLabel: (id: number) => `Retry job ${id}`,
      discardLabel: (id: number) => `Discard job ${id}`,
      noError: 'No error recorded',
      noPayload: 'No ids',
      empty: 'No dead jobs.',
      emptyBody: 'Jobs land here after 5 failed attempts.',
      confirmRetry: {
        title: (id: number, type: string) => `Retry job ${id} (${type})?`,
        body: 'It goes back to the queue with 5 fresh attempts and runs within seconds. The retry is audited.',
        confirm: 'Retry job',
      },
      confirmDiscard: {
        title: (id: number, type: string) => `Discard job ${id} (${type})?`,
        body: 'It leaves this list and never runs again. The row is kept for the record and the discard is audited.',
        confirm: 'Discard job',
      },
    },
    /**
     * W3-17 (M8, spec §16.2): per org and week, the share of called site-days filed on the call.
     * Plain strings for `t()`: the card speaks the panel's language (EN, TR).
     */
    outboundCalls: {
      title: 'Reminder calls (M8)',
      hint: 'Per organisation and week: the site-days TalkOlog called a reporter about, and how many were filed on that call by the reporter’s yes, with no office help. M8’s target is 85%.',
      columns: {
        org: 'Organisation',
        week: 'Week of',
        called: 'Called',
        filed: 'Filed on the call',
        share: 'Share',
      },
      share: '{{pct}}%',
      belowTarget: 'Below the 85% target',
      demo: 'Demo',
      empty: 'No reminder calls in the last 8 weeks.',
    },
    /** Spec §19 Q44 (W1-16): unknown contacts no org sees. */
    orgless: {
      title: 'Unknown contacts with no organisation',
      hint: 'Texts from numbers with no history in exactly one organisation of their country. No customer sees them; give one to the organisation it belongs to, or ignore it.',
      columns: {
        contact: 'Number',
        message: 'Message',
        received: 'Received',
        org: 'Organisation',
      },
      orgLabel: (phone: string) => `Organisation for ${phone}`,
      orgPlaceholder: 'Choose an organisation',
      assign: 'Assign',
      ignore: 'Ignore',
      assigned: 'Contact assigned. The organisation sees it on its Overview and Team page.',
      ignored: 'Contact ignored.',
      empty: 'No unknown contacts without an organisation.',
      thing: 'unknown contacts',
    },
    cost: {
      title: 'Cost outliers',
      hint: 'Filed reports that cost more than 3× the median of their kind over the previous 30 days (once 20 reports exist). AI plus WhatsApp templates, estimated.',
      columns: {
        report: 'Report',
        org: 'Org',
        site: 'Site',
        filed: 'Filed',
        cost: 'Cost',
        median: 'Median',
        ratio: 'vs median',
      },
      ratio: (r: number) => `${r.toFixed(1)}×`,
      breakdown: (ai: string, templates: number) => `AI ${ai} · ${plural(templates, 'template')}`,
      sample: (n: number) => `${plural(n, 'report')} in baseline`,
      otherOrg: 'Switch to this org to open the report',
      empty: 'No cost outliers in the last 30 days.',
    },
    /**
     * W4-10 (spec §19 Q234, Q236): every org's billing; TalkOlog staff set an org's price and a
     * manual org's status (both audited). /ops stays English (§19 Q240).
     */
    billing: {
      title: 'Billing per organisation',
      hint: 'The price per active site and month of each organisation (TalkOlog sets it; a new organisation gets its currency’s default) and the status of the ones TalkOlog invoices directly. A Stripe organisation’s status follows Stripe.',
      columns: {
        org: 'Organisation',
        provider: 'Billed by',
        status: 'Status',
        price: 'Price per site',
        sites: 'Active sites',
      },
      providers: { stripe: 'Stripe', manual: 'TalkOlog (manual)' },
      demo: 'Demo',
      noPrice: 'Not set',
      edit: 'Edit',
      editLabel: (org: string) => `Edit the billing of ${org}`,
      dialogTitle: (org: string) => `Billing of ${org}`,
      dialogBody:
        'The price shows on the organisation’s Billing page and is what Checkout bills; a different price needs a matching Stripe price. Leave the amount empty to clear it.',
      amount: 'Price per site and month',
      amountHint: 'In the currency’s main unit, e.g. 399 or 399.00.',
      amountInvalid: 'Enter an amount above zero with at most two decimals, or leave it empty.',
      currency: 'Currency',
      currencyInvalid: 'Enter a three-letter ISO currency code, e.g. USD or TRY.',
      status: 'Billing status (manual organisations only)',
      statuses: {
        trialing: 'Free trial',
        active: 'Active',
        past_due: 'Past due',
        canceled: 'Canceled',
        paused: 'Paused',
      },
      save: 'Save',
      saved: 'Billing saved. The change is audited.',
      empty: 'No organisations.',
      thing: 'billing',
    },
    toasts: {
      retried: (id: number) => `Job ${id} is queued again.`,
      discarded: (id: number) => `Job ${id} discarded.`,
    },
    notDead: 'Only dead jobs can be retried or discarded.',
  },

  errors: {
    generic: 'Something went wrong. Try again.',
    network: 'Network error. Check your connection and retry.',
    notAllowed: 'You’re not allowed to do that.',
    notFound: 'Not found.',
    demoReadOnly: 'Demo mode: this action is simulated in memory.',
  },

  toasts: {
    saved: 'Saved.',
    signedOff: (no: string) => `${no} signed off.`,
    signedOffReport: '{{no}} signed off.',
    undone: 'Sign-off undone.',
    noWork: (site: string) => `${site} marked as no work.`,
    versionSaved: (v: number) => `Version ${v} saved.`,
    versionSavedN: 'Version {{version}} saved.',
    invoiceAdded: 'Invoice added.',
    notificationsRead: 'Marked as read.',
    termReviewed: 'Glossary updated.',
    termAdded: 'Term added.',
    settingsSaved: 'Settings saved.',
    sitePaused: (site: string) => `${site} paused.`,
    siteResumed: (site: string) => `${site} resumed.`,
    /** W4-10: the same toasts in the panel's language (Billing, Site settings). */
    sitePausedName: '{{site}} paused.',
    siteResumedName: '{{site}} resumed.',
    siteCreated: '{{site}} connected.',
    siteUpdated: 'Site updated.',
    packRequested: 'Evidence pack requested.',
    csvReady: 'Payroll CSV ready.',
    companyResolved: 'Company resolved. New version saved.',
    orgCreated: '{{name}} created.',
    orgUpdated: 'Company details saved.',
    logoUpdated: 'Logo updated.',
    logoRemoved: 'Logo removed.',
  },

  /** Today's PDFs / Export PDFs (.zip) on the Overview and Reports: the panel's language (W4-09). */
  pdfZip: {
    preparing: 'Preparing PDFs…',
    progress: 'Downloading PDFs · {{done}} of {{total}}',
    making: 'Making PDFs · {{done}} of {{total}}',
    ready_one: '{{count}} PDF zipped.',
    ready_other: '{{count}} PDFs zipped.',
    partial_one: '{{count}} PDF zipped · {{skipped}} not ready or failed to download.',
    partial_other: '{{count}} PDFs zipped · {{skipped}} not ready or failed to download.',
    none: 'None of these reports has a PDF yet.',
    failed: 'Couldn’t build the zip. Try again.',
    capped: 'Exporting the first {{limit}} of {{total}} reports. Narrow the filters for the rest.',
    fileName: (label: string, date: string) => `TalkOlog_${label}_${date}.zip`,
    labels: { today: 'daily-reports', archive: 'reports' },
  },

  /** /try — the in-browser field simulator (demo mode only). */
  try: {
    title: 'Try it on WhatsApp',
    eyebrow: 'Field simulator · demo',
    subtitle:
      'Be the site manager: send a voice note, answer the agent, file the report — and watch the office side update.',
    honesty:
      'Simulated in your browser: no WhatsApp, no AI, nothing leaves this page. Voice notes are scripted test cases (a transcript, no audio). The agent’s messages use the same rules and wording as the live agent; where it would use AI, the simulator uses a stored answer or tells you in a grey note.',
    startOver: 'Start over',
    startOverHint: 'Clears this chat and today’s report for the site',
    begin: {
      note: (first: string) => `Send ${first}’s voice note`,
      typed: (first: string) => `Send ${first}’s typed report`,
      pick: 'Pick another voice note',
    },
    phone: {
      region: (name: string) => `Simulated WhatsApp chat with ${name}`,
      title: 'TalkOlog',
      subtitle: (name: string, site: string) => `${name} · ${site}`,
      simulated: 'Simulated',
      log: 'Conversation',
      typing: 'TalkOlog is typing…',
      fromAgent: 'TalkOlog',
      fromReporter: (first: string) => first,
      sentAt: (who: string, time: string) => `${who}, ${time}`,
      voiceNote: 'Voice note',
      transcript: 'Transcript',
      showTranscript: 'Show full transcript',
      hideTranscript: 'Show less',
      photoAlt:
        'Sample site photo: a concrete deck with rebar mats, a tower crane and an overcast sky',
      document: 'PDF document',
      buttons: 'Reply options',
      reminderHistory: 'Sent earlier today',
      system: 'Simulator',
    },
    composer: {
      voice: 'Voice note',
      voiceLabel: 'Send voice note',
      photo: 'Send photo',
      input: 'Type a reply',
      placeholder: 'Type a reply or a command…',
      send: 'Send',
      busy: 'TalkOlog is replying…',
      suggestions: 'Try next',
    },
    sheet: {
      title: 'Send a voice note',
      description:
        'Test cases from the TalkOlog evaluation set, each from a Kestrel site and its site manager. The simulator uses the report the extraction is expected to produce from each transcript.',
      send: 'Send',
      sendLabel: (title: string) => `Send: ${title}`,
      typed: 'Typed report',
      current: 'This chat',
      switches: (name: string) => `Starts a chat as ${name}`,
      clearsUnfinished: (site: string) =>
        `Your unfinished chat at ${site} is cleared, and so is its report.`,
      replaces: (site: string, reportNo: string | null) =>
        reportNo
          ? `Replaces today’s demo report for ${site} (${reportNo}).`
          : `Replaces today’s demo status for ${site}.`,
      fresh: (site: string) => `${site} has nothing filed today yet.`,
      recordedOn: (date: string) => `Test case from ${date}, replayed as today`,
      languages: (codes: string[]) =>
        codes
          .map((c) => ({ en: 'English', es: 'Spanish', pl: 'Polish', hi: 'Hindi' })[c] ?? c)
          .join(' + '),
      outcomes: {
        draft: 'Complete note',
        question: 'Agent asks',
        weather_day: 'Weather day',
        no_work: 'No work',
      },
    },
    scenarios: {
      'riverside-2026-10-09': {
        title: 'Marco’s Friday report',
        shows: 'Spanish and English mixed; the Delta crew size is missing, so the agent asks.',
      },
      'oakmont-2026-10-08-missing-headcount': {
        title: '“A few of the Delta guys”',
        shows: 'Two gaps, asked one at a time: Delta’s headcount, then Summit’s hours.',
      },
      'pier9-2026-10-09-rain-unknown-roofer': {
        title: 'Rain on the roof, a new roofer',
        shows: '2 h lost to rain and an unknown subcontractor the office has to confirm.',
      },
      'elm-2026-10-02-rain-morning': {
        title: 'Rained out until 10',
        shows: 'Weather stoppage logged as a delay; a complete note, straight to the draft.',
      },
      'northgate-2026-10-08-overtime-pour': {
        title: 'Overtime pour',
        shows: 'End times and overtime turned into hours in code; nothing to ask.',
      },
      'riverside-2026-10-07-spanish': {
        title: 'All in Spanish',
        shows: 'Spoken in Spanish, filed in English, with “hasta las dos” turned into hours.',
      },
      'northgate-2026-10-06-correction-near-miss': {
        title: '“Scratch that, six guys”',
        shows: 'A correction inside the note, and a near miss logged as an incident.',
      },
      'hillcrest-2026-10-06-full-weather-day': {
        title: 'Full weather day',
        shows: 'Nobody worked: logged as a weather day for the delay record, not a daily report.',
      },
      'elm-2026-10-08-no-work': {
        title: 'Street closed, no work',
        shows: 'A no-work day: recorded as such, no crew or hours invented.',
      },
      'elm-2026-10-07-text-report': {
        title: 'Typed instead of spoken',
        shows: 'A text message works too; no photo was sent, so the agent asks for one.',
      },
    } as Record<string, { title: string; shows: string }>,
    notes: {
      intro: (first: string, name: string, site: string, typed: boolean) =>
        `You are ${name}, site manager at ${site}. Send ${first}’s ${typed ? 'typed report' : 'voice note'} and reply to the agent as ${first} would. The chat works like WhatsApp; the office side updates as you go.`,
      introLate: (clock: string, deadline: string) =>
        `The demo clock is ${clock}, after the ${deadline} deadline, so reports filed here show as late.`,
      introReplaces: (what: string) =>
        `Sending replaces today’s demo report for this site (${what}).`,
      replaced: (site: string, reportNo: string) =>
        reportNo
          ? `Today’s demo report for ${site} (${reportNo}) was cleared so this chat files a fresh one.`
          : `Today at ${site} was cleared so this chat starts fresh.`,
      unsupported_text:
        'The live agent uses the AI to understand free text like this. In the simulator, try a button, “no work today”, “rained out, sent everyone home at 11”, “help” or “thanks”.',
      unsupported_answer: (prompt: string, field: string) =>
        `The live agent uses the AI to read answers like this. Here, tap a button or answer in a simple form: ${
          (
            {
              'manpower.workers': '“5, full day” or “6 guys till 3”',
              'manpower.hours': '“full day”, “half day” or “till 3”',
              'weather.impact': '“no stoppage” or “2 hours”',
              safety: '“no incidents” or “toolbox talk, no incidents”',
              'company.unknown': '“add”, or the company’s name',
              photo: 'send a photo',
            } as Record<string, string>
          )[field] ?? '“5, full day”'
        }. (${prompt})`,
      unsupported_fix:
        'The live agent uses the AI to understand this kind of correction. In the simulator, name the crew and the number, e.g. “Apex had 7 not 6” or “Summit was there till 3”.',
      unchanged_fix: 'The report already says that, so nothing changed.',
      unsupported_report:
        'The live agent extracts any typed report with the AI. In the simulator, send one of the recorded voice notes instead.',
      voice_busy: (first: string) =>
        `The live agent would read another note from ${first} now as an answer or a fix. In the simulator, voice notes are the recorded cases: use Start over to send a different one.`,
      edit_other_day: (weekday: string, example: boolean) =>
        `The live agent can edit any report from the last 14 days. The simulator only has today’s report${
          example ? `: try “edit ${weekday} — Apex was 7 guys, not 6”.` : '.'
        }`,
      silent: 'The live agent does not reply to that here, so nothing happens.',
    },
    suggestions: {
      sendNote: (first: string) => `Send ${first}’s voice note`,
      pickAnother: 'Pick another voice note',
      noWork: 'no work today',
      rainedOut: 'rained out, sent everyone home at 11',
      fix: 'Apex had 7 not 6',
      edit: (weekday: string) => `edit ${weekday} — Apex was 7 guys, not 6`,
      typed: (text: string) => `Send “${text}”`,
      ok: 'OK',
      photo: 'Send a photo',
      openReport: 'Open the report in the panel',
    },
    office: {
      title: 'What the office sees',
      today: (date: string) => `Today · ${date}`,
      site: 'Site',
      status: 'Status',
      report: 'Report',
      notFiled: 'Not filed yet',
      open: 'Open the report in the panel',
      live: 'Overview, Sites and Reports update live as you chat.',
      legend: 'What happened',
      empty: 'Nothing yet. Send a voice note to start.',
      events: {
        replaced: (no: string | null) =>
          no ? `Cleared today’s demo report ${no}` : 'Cleared today’s demo status',
        received: (voice: boolean, duration: string, photos: number) =>
          `${voice ? `Voice note${duration ? ` (${duration})` : ''}` : 'Typed report'}${photos ? ` + ${photos === 1 ? '1 photo' : `${photos} photos`}` : ''} received`,
        question: (prompt: string) => `Asked: ${prompt}`,
        answered: (answer: string, ack: string | null) =>
          ack
            ? `Answer “${answer}” applied — ${ack.replace(/^Thanks — /, '')}`
            : `Answered “${answer}”`,
        photo_answered: 'Photo received — question answered',
        unanswered: (prompt: string) => `No usable answer; flagged for review: ${prompt}`,
        draft: 'Draft sent for confirmation',
        corrected: (note: string) => `Draft corrected: ${note}`,
        filed: (no: string, review: boolean) =>
          review ? `Filed ${no} — flagged for review` : `Filed ${no}`,
        weather_day: (no: string) => `Weather day logged as ${no}`,
        no_work: 'Day recorded as no work',
        version: (no: string, v: number, note: string) => `${no} version ${v}: ${note}`,
        company_added: (name: string) => `${name} added as an unconfirmed subcontractor`,
        voided: (no: string) => `${no} voided: the note described a day off`,
      },
    },
    filedCallout: (no: string) => `Filed as ${no}. The office has it now.`,
  },

  a11y: {
    statusChip: (label: string) => `Status: ${label}`,
    /** `t()` forms of the shared components (StatusChip, KpiTile), W1-16. */
    statusChipLabel: 'Status: {{label}}',
    lateBadge: 'Filed after the deadline',
    kpiBar: (parts: string) => `Breakdown: ${parts}`,
    kpiBreakdown: 'Breakdown: {{parts}}',
    siteCard: (name: string) => `Site card: ${name}`,
    menuFor: (name: string) => `Menu for ${name}`,
    menuForName: 'Menu for {{name}}',
    required: 'required',
    searchResults: 'Search results',
    loadingPage: 'Loading page',
    closeDialog: 'Close dialog',
    heatmapLegend: 'Hours lost, light to dark',
  },
} as const;

export type Copy = typeof copy;
export default copy;
